import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import Header from "../../Components/Header";
import { useNavigate } from "react-router-dom";
import {
  getHotelPricing,
  bookHotel,
  holdHotel,
  hotelApproval,
  SaveHotelDetails,
} from "../../Services/HotelSearchApi";
import {
  GetCorporates,
  GetCorporateDetails,
  CollectPayment,
  GetTravellersByCorporateCode,
  CreateCorporateTravellerProfile,
  GetOrganizationsType,
} from "../../Services/API";
import { ApprovalRequest } from "../../Services/ApprovalAPI";
import { SendPaymentLinkMail } from "../../Services/PaymentLink";
import { AuthContext } from "../../Routes/AuthProvider";

const cleanPhoneNumber = (phone) => {
  if (!phone) return "";
  let cleaned = String(phone).trim();
  if (cleaned.startsWith("+91")) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith("91") && cleaned.replace(/[^0-9]/g, "").length === 12) {
    cleaned = cleaned.substring(2);
  }
  return cleaned.replace(/[^0-9]/g, "");
};

export default function HotelBooking() {  
  const navigate = useNavigate();
  const { userName, roles, corporateId } = useContext(AuthContext);

  const [activeStep, setActiveStep] = useState(1);
  const [hotelData, setHotelData] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [expandedItems, setExpandedItems] = useState({
    base: false,
    fees: false,
    markup: false,
  });
  const [manualMarkup, setManualMarkup] = useState(0);
  const [error, setError] = useState(null);
  const [travellerDetails, setTravellerDetails] = useState([]);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [corporateList, setCorporateList] = useState([]);
  const [selectedCorporate, setSelectedCorporate] = useState(
    localStorage.getItem("selectedCorporateCode") || "",
  );
  const [corporateInfo, setCorporateInfo] = useState(null);
  const [gstNumber, setGstNumber] = useState("");
  const [gstName, setGstName] = useState("");
  const [gstEmail, setGstEmail] = useState("");
  const [gstPhone, setGstPhone] = useState("");
  const [gstAddress, setGstAddress] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isHoldingBooking, setIsHoldingBooking] = useState(false);
  const [isSendingPaymentLink, setIsSendingPaymentLink] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [travellerList, setTravellerList] = useState([]);
  const [selectedTravellerId, setSelectedTravellerId] = useState("");
  const [organizationsDetails, setOrganizationsDetails] = useState({
    billTo: localStorage.getItem("selectedCorporateCode") || "",
    companyType: "",
    companyName: "",
    companyId: "",
    cashBalance: 0,
    userEmailId: "",
    billingOptions: [],
  });

  const [paymentError, setPaymentError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const isTmc =
    roles?.includes("TMC Operations") || roles?.includes("TMC Manager");
  const isCorporateTraveller =
    roles?.includes("Corporate Traveller") ||
    roles?.includes("Corp Administrator");
  const isBookingApprovalRequired =
    corporateInfo?.isBookingApprovalRequired === "True" ||
    corporateInfo?.isBookingApprovalRequired === true;

  // Flatten child ages from all rooms for validation
  const requiredChildAges = useMemo(() => {
    return (
      searchData?.roomGuests?.reduce((acc, room) => {
        return acc.concat(room.childAge || []);
      }, []) || []
    );
  }, [searchData]);

  const clearHotelSession = () => {
    sessionStorage.removeItem("selectedHotel");
    sessionStorage.removeItem("selectedRoom");
    sessionStorage.removeItem("hotelSearchData");
  };

  // ── Emulation Sync (polling localStorage) ──
  useEffect(() => {
    const interval = setInterval(() => {
      const storedCorpCode =
        localStorage.getItem("selectedCorporateCode") || "";
      if (storedCorpCode !== selectedCorporate) {
        setSelectedCorporate(storedCorpCode);
        setOrganizationsDetails((prev) => ({
          ...prev,
          billTo: storedCorpCode,
        }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedCorporate]);

  const handleTravellerChange = (index, field, value) => {
    // Alphabet only validation for names
    if (field === "firstName" || field === "lastName") {
      value = value.replace(/[^A-Za-z\s]/g, "");
    }

    setTravellerDetails((prev) => {
      const updated = [...prev];
      if (!updated[index]) updated[index] = {};
      updated[index][field] = value;
      return updated;
    });

    // Inline validation for Age (Adults & Children)
    if (field === "age") {
      if (travellerDetails[index].type === "Child") {
        const childPointer = travellerDetails
          .slice(0, index)
          .filter((t) => t.type === "Child").length;
        const requiredAge = requiredChildAges[childPointer];

        if (Number(value) !== Number(requiredAge)) {
          setFieldErrors((prev) => ({
            ...prev,
            [`traveller_${index}_age`]: `Age must match search (${requiredAge} yr)`,
          }));
        } else {
          setFieldErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`traveller_${index}_age`];
            return newErrors;
          });
        }
      } else {
        // Adult age validation
        if (!value || Number(value) < 18) {
          setFieldErrors((prev) => ({
            ...prev,
            [`traveller_${index}_age`]: "Adult must be 18+ years old",
          }));
        } else {
          setFieldErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`traveller_${index}_age`];
            return newErrors;
          });
        }
      }
    }

    // Inline validation for PAN
    if (field === "pan") {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (value && !panRegex.test(value.toUpperCase())) {
        setFieldErrors((prev) => ({
          ...prev,
          [`traveller_${index}_pan`]: "Invalid PAN (e.g. ABCDE1234F)",
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`traveller_${index}_pan`];
          return newErrors;
        });
      }
    }

    // Inline validation for Passport
    if (field === "passport") {
      const passportRegex = /^[A-Z0-9]{6,15}$/;
      if (value && !passportRegex.test(value.toUpperCase())) {
        setFieldErrors((prev) => ({
          ...prev,
          [`traveller_${index}_passport`]: "Invalid Passport Number",
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`traveller_${index}_passport`];
          return newErrors;
        });
      }
    }
  };



  const handleContactEmailChange = (val) => {
    setContactEmail(val);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (val && !emailRegex.test(val)) {
      setFieldErrors((prev) => ({ ...prev, contactEmail: "Invalid email" }));
    } else {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.contactEmail;
        return newErrors;
      });
    }
  };

  const handleContactPhoneChange = (val) => {
    const cleaned = cleanPhoneNumber(val);
    setContactPhone(cleaned);
    if (cleaned && cleaned.length !== 10) {
      setFieldErrors((prev) => ({
        ...prev,
        contactPhone: "Phone must be 10 digits",
      }));
    } else {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.contactPhone;
        return newErrors;
      });
    }
  };

  const toggleItem = (item) => {
    setExpandedItems((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const pricingFetched = React.useRef(false);

  useEffect(() => {
    const fetchDynamicPricing = async () => {
      if (pricingFetched.current) return;
      pricingFetched.current = true;

      setLoading(true);
      const storedHotel = sessionStorage.getItem("selectedHotel");
      const storedRoom = sessionStorage.getItem("selectedRoom");
      const storedSearch = sessionStorage.getItem("hotelSearchData");

      if (storedHotel && storedRoom && storedSearch) {
        const hotel = JSON.parse(storedHotel);
        const room = JSON.parse(storedRoom);
        const search = JSON.parse(storedSearch);

        setHotelData(hotel);
        setRoomData(room);
        setSearchData(search);

        // Initialize Traveller Details state based on search criteria - Grouped by Room
        const initialTravellers = [];
        search.roomGuests.forEach((room) => {
          // Add Adults for this room
          for (let i = 0; i < room.noOfAdults; i++) {
            initialTravellers.push({
              type: "Adult",
              title: "Mr",
              firstName: "",
              lastName: "",
              age: "",
              pan: "",
              passport: "",
            });
          }
          // Add Children for this room
          const childAges = room.childAge || [];
          for (let i = 0; i < (room.noOfChilds || 0); i++) {
            initialTravellers.push({
              type: "Child",
              title: "Master",
              firstName: "",
              lastName: "",
              age: String(childAges[i] || ""),
              pan: "",
              passport: "",
            });
          }
        });
        setTravellerDetails(initialTravellers);

        try {
          const payload = {
            corporateTraveler: localStorage.getItem("userName") || "",
            corporateCode: localStorage.getItem("agencyId") || "",
            travelerType: search.travelType || "1",
            traceId: search.traceId,
            roomPlan: {
              roomId: room.roomId || room.roomIndex,
              rateId: room.rateId || room.rateCode,
            },
            roomsId: room.roomId || room.roomIndex,
            supplierParameter: room.supplierParameter || "",
            key: hotel.key || hotel.Key || "",
            bookingRefNo: room.bookingRefNo || "",
          };

          const response = await getHotelPricing(payload);
          if (response && response.status === 1) {
            setPricingData(response);
            setError(null);
            const responseMarkup =
              response.markupInfo?.markupAmount ||
              response.data?.rooms?.[0]?.priceDetails?.markup ||
              response.data?.rooms?.[0]?.markup ||
              0;
            setManualMarkup(responseMarkup);
          } else {
            const errorMsg =
              response?.errors?.[0]?.errorDetail ||
              "Pricing failed. Please try again.";
            setError(errorMsg);
            setPricingData(null);
          }
        } catch (error) {
          console.error("Pricing API failed:", error);
          setError(
            "Something went wrong while fetching pricing. Please try again later.",
          );
        }
      }
      setLoading(false);
    };

    fetchDynamicPricing();
  }, []);

  // Synchronize with localStorage (e.g., when Header modal updates emulation)
  useEffect(() => {
    const syncWithLocalStorage = () => {
      const storedCorpCode = localStorage.getItem("selectedCorporateCode");
      if (storedCorpCode && storedCorpCode !== selectedCorporate) {
        setSelectedCorporate(storedCorpCode);
      }
    };

    window.addEventListener("storage", syncWithLocalStorage);
    // Check every second to catch same-page updates from the modal
    const interval = setInterval(syncWithLocalStorage, 1000);

    return () => {
      window.removeEventListener("storage", syncWithLocalStorage);
      clearInterval(interval);
    };
  }, [selectedCorporate]);

  // Fetch Corporate Details and List
  useEffect(() => {
    const fetchDetailedCorporateInfo = async () => {
      const storedCorpCode = localStorage.getItem("selectedCorporateCode");
      const storedUserId = localStorage.getItem("selectedUserId");
      const userName = localStorage.getItem("userName");

      if (storedCorpCode || userName) {
        try {
          const params = {
            UserName: userName || "",
          };

          if (isTmc) {
            params.CorporateCode = selectedCorporate || storedCorpCode || "";
            params.EmulateUser = storedUserId || "";
          }

          const response = await GetCorporateDetails(params);
          if (response && response.corporateDetails) {
            const details = response.corporateDetails;
            setCorporateInfo(details);

            // 1. Bind GST Details
            setGstNumber(details.gstNumber || "");
            setGstName(details.corporateName || details.gstName || "");
            setGstEmail(details.businessEmailID || details.gstEmail || "");
             setGstPhone(
              cleanPhoneNumber(
                details.mobileNumber || details.gstContactNumber || ""
              )
            );
            setGstAddress(
              details.registeredAddressLine1 || details.gstAddress || "",
            );

            // 2. Bind Contact Details
            const targetEmail =
              details.email ||
              details.businessEmailID ||
              (isTmc ? storedUserId : "") ||
              userName ||
              details.gstEmail ||
              "";
            setContactEmail(targetEmail || contactEmail);
            setContactPhone(
              cleanPhoneNumber(
                details.phoneNumber ||
                  details.mobileNumber ||
                  details.gstContactNumber ||
                  ""
              )
            );
            const rawDial = details.phoneCode || details.dialCode || "+91";
            setDialCode(rawDial.startsWith("+") ? rawDial : `+${rawDial}`);

            // 3. Bind Traveller 1 (Adult 1) Details
            setTravellerDetails((prev) => {
              // If prev is empty, we initialize it here as a fallback
              let updated = [...prev];
              if (updated.length === 0 && searchData) {
                searchData.roomGuests.forEach((room) => {
                  // Add Adults for this room
                  for (let i = 0; i < room.noOfAdults; i++) {
                    updated.push({
                      type: "Adult",
                      title: "Mr",
                      firstName: "",
                      lastName: "",
                      age: "",
                    });
                  }
                  // Add Children for this room
                  const childAges = room.childAge || [];
                  for (let i = 0; i < (room.noOfChilds || 0); i++) {
                    updated.push({
                      type: "Child",
                      title: "Master",
                      firstName: "",
                      lastName: "",
                      age: String(childAges[i] || ""),
                    });
                  }
                });
              }

              if (updated.length > 0) {
                const firstAdultIdx = updated.findIndex(
                  (t) => t.type === "Adult",
                );
                if (firstAdultIdx !== -1) {
                  // Always bind if details are present in profile
                  if (details.firstName)
                    updated[firstAdultIdx].firstName = details.firstName;
                  if (details.lastName)
                    updated[firstAdultIdx].lastName = details.lastName;
                  if (details.panNumber || details.pan)
                    updated[firstAdultIdx].pan =
                      details.panNumber || details.pan;
                  if (details.passportNumber || details.passport)
                    updated[firstAdultIdx].passport =
                      details.passportNumber || details.passport;
                  if (details.dob) {
                    try {
                      const birthDate = new Date(details.dob);
                      const today = new Date();
                      let age = today.getFullYear() - birthDate.getFullYear();
                      const m = today.getMonth() - birthDate.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                      }
                      updated[firstAdultIdx].age = String(age);
                    } catch (e) {}
                  }
                }
              }
              return updated;
            });
          }
        } catch (error) {
          console.error("Error fetching detailed corporate info:", error);
        }
      }
    };

    const fetchTravellerList = async () => {
      if (isTmc && selectedCorporate) {
        try {
          const data = await GetTravellersByCorporateCode(selectedCorporate);
          setTravellerList(data || []);
        } catch (error) {
          console.error("Error fetching traveller list:", error);
        }
      }
    };

    fetchDetailedCorporateInfo();
    fetchTravellerList();

    const fetchOrganizationsType = async () => {
      try {
        const response = await GetOrganizationsType(userName);
        const agency = response.data?.agencyDetails;
        if (!agency) return;

        const billingOptions = [
          {
            text:
              agency.companyType === "LegalEntity"
                ? "Legal Entity"
                : agency.companyType,
            value: agency.companyId,
          },
        ];

        setOrganizationsDetails((prev) => ({
          ...prev,
          companyType: agency.companyType,
          companyId: agency.companyId,
          cashBalance: agency.cashBalance,
          userEmailId: agency.userName,
          billingOptions,
          billTo: agency.companyId,
        }));
      } catch (err) {
        console.error("Failed to load organization details:", err);
      }
    };
    fetchOrganizationsType();

    if (isTmc || roles.includes("Corporate")) {
      const fetchCorporateList = async () => {
        try {
          const userName = localStorage.getItem("userName");
          const storedCorpCode = localStorage.getItem("selectedCorporateCode");
          const data = await GetCorporates(userName);
          if (data && data.length > 0) {
            setCorporateList(data);
            // Auto-select logic
            if (data.length === 1) {
              setSelectedCorporate(data[0].corporateCode || data[0].value);
            } else if (storedCorpCode) {
              // If emulated in header, select it in the dropdown
              const exists = data.some(
                (c) => (c.corporateCode || c.value) === storedCorpCode,
              );
              if (exists) setSelectedCorporate(storedCorpCode);
            }
          }
        } catch (error) {
          console.error("Error fetching corporate list:", error);
        }
      };
      fetchCorporateList();
    }
  }, [isTmc, selectedCorporate]);

  const handleCorporateChange = (e) => {
    setSelectedCorporate(e.target.value);
    setSelectedTravellerId(""); // Reset traveller selection when corporate changes
  };

  const handleTravellerSelectionChange = async (e) => {
    const travellerId = e.target.value;
    console.log("Selected Traveller ID:", travellerId);
    setSelectedTravellerId(travellerId);

    if (travellerId) {
      const traveller = travellerList.find(
        (t) => String(t.id || t.userId || t.value) === String(travellerId),
      );
      console.log("Found Traveller:", traveller);

      if (traveller) {
        // Dynamically bind to Contact Details
        if (traveller.businessEmailID || traveller.email)
          setContactEmail(traveller.businessEmailID || traveller.email);
        if (traveller.mobileNumber || traveller.phone)
          setContactPhone(cleanPhoneNumber(traveller.mobileNumber || traveller.phone));
        if (traveller.phoneCode || traveller.dialCode) {
          const rawDial = traveller.phoneCode || traveller.dialCode;
          setDialCode(rawDial.startsWith("+") ? rawDial : `+${rawDial}`);
        }

        // Dynamically bind to Adult 1
        setTravellerDetails((prev) => {
          const updated = [...prev];
          const firstAdultIdx = updated.findIndex((t) => t.type === "Adult");
          if (firstAdultIdx !== -1) {
            updated[firstAdultIdx] = {
              ...updated[firstAdultIdx],
              firstName: traveller.firstName || "",
              lastName: traveller.lastName || "",
              pan: traveller.panNumber || traveller.pan || "",
              passport: traveller.passportNumber || traveller.passport || "",
            };

            // If DOB exists, calculate age and bind it
            if (traveller.dob) {
              try {
                const birthDate = new Date(traveller.dob);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                  age--;
                }
                updated[firstAdultIdx].age = String(age);
              } catch (e) {
                console.error("Error calculating age from DOB:", e);
              }
            }
          }
          return updated;
        });
      }
    } else {
      // Reset if "Choose Traveller" is selected
      setSelectedTravellerId("");
    }
  };

  const validateStep1 = (customTravellerDetails, customEmail, customPhone) => {
    const details = customTravellerDetails || travellerDetails;
    const email = customEmail || contactEmail;
    const phone = customPhone || contactPhone;

    // 1. Check Contact Details
    if (!email || !phone) {
      alert("Please enter both contact email and phone number.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address.");
      return false;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) {
      alert("Please enter a valid 10-digit phone number.");
      return false;
    }

    // 2. Check Traveller Details
    const isPanRequired = pricingData?.data?.rooms?.[0]?.isPanRequired;
    const isPassportRequired =
      pricingData?.data?.rooms?.[0]?.isPassportRequired;

    for (let i = 0; i < details.length; i++) {
      const traveller = details[i];
      const isChild = traveller?.type === "Child";
      if (
        !traveller ||
        !traveller.firstName ||
        traveller.firstName.trim().length < 2 ||
        !traveller.lastName ||
        traveller.lastName.trim().length < 1 ||
        (isChild && !traveller.age)
      ) {
        alert(
          `Please enter a valid first name and last name${isChild ? ", and age" : ""} for ${traveller?.type || "Traveller"} ${i + 1}.`,
        );
        return false;
      }

      // PAN validation — required if flag is set OR field has pre-filled data
      if ((isPanRequired || traveller.pan) && !traveller.pan) {
        alert(
          `Please enter PAN Number for ${traveller?.type || "Traveller"} ${i + 1}.`,
        );
        return false;
      }

      if (traveller.pan) {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(traveller.pan.toUpperCase())) {
          alert(
            `Please enter a valid PAN Number for ${traveller?.type || "Traveller"} ${i + 1} (e.g., ABCDE1234F).`,
          );
          return false;
        }
      }

      // Passport validation — required if flag is set OR field has pre-filled data
      if ((isPassportRequired || traveller.passport) && !traveller.passport) {
        alert(
          `Please enter Passport Number for ${traveller?.type || "Traveller"} ${i + 1}.`,
        );
        return false;
      }

      if (traveller.passport) {
        const passportRegex = /^[A-Z0-9]{6,15}$/;
        if (!passportRegex.test(traveller.passport.toUpperCase())) {
          alert(
            `Please enter a valid Passport Number for ${traveller?.type || "Traveller"} ${i + 1}.`,
          );
          return false;
        }
      }
    }

    // 3. Check Corporate Selection if user is TMC
    if (isTmc && corporateList.length > 0 && !organizationsDetails.billTo) {
      alert("Please select a billing corporate from Billing Details.");
      return false;
    }

    // 4. GST Validation (Mandatory for Corporate Billing, optional for others unless filled)
    const isGstRequired = isTmc || isCorporateTraveller || !!(gstNumber || gstName || gstEmail || gstPhone || gstAddress);

    if (isGstRequired) {
      if (!gstNumber || !gstName || !gstEmail || !gstPhone || !gstAddress) {
        alert(
          "Please fill all GST details (GST Number, Name, Email, Phone, and Address).",
        );
        return false;
      }

      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
      if (!gstRegex.test(gstNumber.trim().toUpperCase())) {
        alert("Enter a valid 15-digit GSTIN.");
        return false;
      }

      if (!emailRegex.test(gstEmail.trim())) {
        alert("Please enter a valid GST email address.");
        return false;
      }

      if (!phoneRegex.test(gstPhone.trim())) {
        alert("Please enter a valid 10-digit GST phone number.");
        return false;
      }
    }

    return true;
  };

  const handlePayment = async () => {
    if (isProcessingPayment) return;

    // Terms & Conditions only required for TMC users going through payment step
    if (isTmc && !isAgreed) {
      alert("Please accept the Terms & Conditions before proceeding.");
      return;
    }

    // Payment Method Validation
    if (isTmc && !paymentMethod) {
      alert("Please select a payment method before continuing.");
      return;
    }

    // Agency Balance/Credit Validation
    if (isTmc) {
      const cashBalance = parseFloat(
        organizationsDetails?.cashBalance ||
          corporateInfo?.availableBalance ||
          0,
      );
      const creditLimit = parseFloat(corporateInfo?.creditLimit || 0);

      if (paymentMethod === "AgencyWallet") {
        if (cashBalance < grandTotal) {
          alert("Insufficient agency balance for payment.");
          return;
        }
      }

      if (paymentMethod === "AgencyCreditLimit") {
        if (cashBalance + creditLimit < grandTotal) {
          alert("Insufficient agency credit limit for payment.");
          return;
        }
      }
    }

    // Terms Checkbox Validation
    if (isTmc && !isAgreed) {
      alert("Please accept the Terms & Conditions before proceeding.");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const selectedCorporateCode =
        localStorage.getItem("selectedCorporateCode") || corporateId || "";
      const selectedUserId =
        localStorage.getItem("selectedUserId") || userName || "";

      const pricingRoom = pricingData?.data?.rooms?.[0] || roomData;

      // Construct roomGuests structure according to new API requirement
      let guestPointer = 0;
      const roomGuests = searchData.roomGuests.map((room, idx) => {
        const adultsCount = room.noOfAdults;
        const childCount = room.noOfChilds || 0;
        const totalGuestsInRoom = adultsCount + childCount;

        const roomGuestsList = travellerDetails
          .slice(guestPointer, guestPointer + totalGuestsInRoom)
          .map((t, gIdx) => ({
            title: t.title || "Mr",
            firstName: t.firstName,
            lastName: t.lastName,
            isLead: gIdx === 0 && idx === 0,
            pan: t.pan || "",
            passportNumber: t.passport || "",
            paxType: t.type === "Adult" ? "ADULT" : "CHILD",
          }));

        guestPointer += totalGuestsInRoom;

        return {
          roomNo: idx + 1,
          noOfAdults: adultsCount,
          noOfChilds: childCount,
          childAge: room.childAge || [],
          guests: roomGuestsList,
        };
      });

      const bookPayload = {
        corporateTraveler: selectedUserId,
        corporateCode: selectedCorporateCode,
        travelerType: searchData.travelType || "1",
        traceId: pricingData.traceId,
        hotelId: hotelData.hotelCode || pricingData.hotelId || "",
        bookingRefNo: pricingData.bookingRefNo || roomData.bookingRefNo || "",
        checkInDate: searchData.checkInDate,
        checkOutDate: searchData.checkOutDate,
        supplierParameter: pricingRoom.supplierParameter || "",
        currency: pricingData.currency || "INR",
        customMarkUp: manualMarkup,
        totalPrice: grandTotal.toFixed(2),
        roomId: pricingRoom.roomId || "",
        rateId: pricingRoom.rateId || "",
        roomGuests: roomGuests,
        contactInfo: {
          email: contactEmail,
          phone: contactPhone,
          dialCode: dialCode,
        },
        isHold: false,
        key: pricingData?.data?.key || "",
        policyType: pricingData.policyType || "",
        policyReason: pricingData.policyReason || "",
        bookingFee: bookingFee,
        managementFee: managementFee,
        managementFeeTax: managementFeeTax,
        markUp: apiMarkup,
        reportingManager: corporateInfo?.reportingManager || "",
        gstinfo: {
          gstNumber: gstNumber,
          name: gstName,
          email: gstEmail,
          phone: gstPhone,
          address: gstAddress,
        },
        CreatedBy: userName,
      };

      // Step 1: Place a Hold booking (isHold: true)
      // const response = await holdHotel(bookPayload);

      // if (
      //   response &&
      //   (response.success || response.confirmationNo || response.status === 1 || response.bookingRefId)
      // ) {
      //   const referenceNo = response.bookingRefId || response.confirmationNo || "";
      //   const sessionId = response.key || pricingData?.data?.key || "";

      // First save hotel details as requested
      await SaveHotelDetails({ ...bookPayload, isHold: true });

      if (isTmc) {
        // Step 2: Collect Payment
        const paymentResponse = await CollectPayment({
          transactionID: pricingData.bookingRefNo,
          referenceNo: pricingData.bookingRefNo,
          corporateCode: selectedCorporateCode,
          userId: selectedUserId,
          service: "Hotel",
          sessionId: pricingData?.data?.key,
          transactionType: "",
          remarks: "",
          totalAmount: grandTotal.toFixed(2),
          creditLimit: true,
          status: "",
          paymentMode: {
            paymentModeType: organizationsDetails?.companyType || "",
            cardType: "",
            productType: "",
            pgProvider: paymentMethod,
          },
          ipAddress: "",
        });

        if (
          paymentResponse?.success === true &&
          paymentResponse?.message === "Payment form generated."
        ) {
          window.open(paymentResponse.formHtml, "_self");
          return;
        } else if (paymentResponse?.success === true) {
          // Step 3: Final Booking Confirmation (isHold: false)
          bookPayload.isHold = false;
          // Update payload with reference from hold if needed
          bookPayload.bookingRefNo = pricingData.bookingRefNo;

          const confirmResponse = await bookHotel(bookPayload);

          if (
            confirmResponse &&
            (confirmResponse.success ||
              confirmResponse.confirmationNo ||
              confirmResponse.status === 1)
          ) {
            clearHotelSession();
            navigate("/hotel/thankyou?refId=" + confirmResponse.bookingRefId);
          } else {
            alert(
              confirmResponse?.message ||
                "Final booking failed. Please contact support.",
            );
          }
        } else {
          alert(
            paymentResponse?.message || "Payment failed. Please try again.",
          );
        }
      } else {
        // Non-TMC: Finalize Booking directly or as per requirement
        bookPayload.isHold = false;
        const confirmResponse = await bookHotel(bookPayload);

        if (
          confirmResponse &&
          (confirmResponse.success ||
            confirmResponse.confirmationNo ||
            confirmResponse.status === 1)
        ) {
          clearHotelSession();
          navigate("/hotel/thankyou?refId=" + confirmResponse.bookingRefId);
        } else {
          alert(
            confirmResponse?.message || "Booking failed. Please try again.",
          );
        }
      }
    } catch (error) {
      console.error("Booking failed:", error);
      alert("An error occurred while booking. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const SubmitApprovalRequest = async () => {
    if (isProcessingPayment) return;

    const pricingRoom = pricingData?.data?.rooms?.[0];
    if (!pricingData || !pricingRoom) {
      alert("Pricing details not available. Please try again.");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const selectedCorporateCode =
        localStorage.getItem("selectedCorporateCode") || corporateId || "";
      const selectedUserId =
        localStorage.getItem("selectedUserId") || userName || "";

      const apiTotalAmount =
        pricingData?.totalPrice || pricingRoom?.price || roomData?.price || 0;
      // const grandTotal = apiTotalAmount + manualMarkup + managementFee;

      // Construct roomGuests structure according to new API requirement
      let guestPointer = 0;
      const roomGuests = searchData.roomGuests.map((room, idx) => {
        const adultsCount = room.noOfAdults;
        const childCount = room.noOfChilds || 0;
        const totalGuestsInRoom = adultsCount + childCount;

        const roomGuestsList = travellerDetails
          .slice(guestPointer, guestPointer + totalGuestsInRoom)
          .map((t, gIdx) => ({
            title: t.title || "Mr",
            firstName: t.firstName,
            lastName: t.lastName,
            isLead: gIdx === 0 && idx === 0,
            pan: t.pan || "",
            passportNumber: t.passport || "",
            paxType: t.type === "Adult" ? "ADULT" : "CHILD",
          }));

        guestPointer += totalGuestsInRoom;

        return {
          roomNo: idx + 1,
          noOfAdults: adultsCount,
          noOfChilds: childCount,
          childAge: room.childAge || [],
          guests: roomGuestsList,
        };
      });

      const travellersSavingProfile = travellerDetails.filter(
        (t) => t.saveInfo,
      );
      if (travellersSavingProfile.length > 0) {
        try {
          await CreateCorporateTravellerProfile({
            contactDetails: {
              emailId: contactEmail,
              countryCode: dialCode,
              mobile: contactPhone,
            },
            travellers: {
              adults: travellersSavingProfile.map((t) => ({
                ...t,
                userName: t.userName || "",
              })),
            },
            userName,
            corporateCode: selectedCorporateCode,
          });
        } catch (profileErr) {
          console.error("Profile creation failed (non-blocking):", profileErr);
        }
      }

      const approvalPayload = {
        corporateTraveler: selectedUserId,
        corporateCode: selectedCorporateCode,
        travelerType: searchData.travelType || "1",
        traceId: pricingData.traceId,
        hotelId: hotelData.hotelCode || pricingData.hotelId || "",
        bookingRefNo: pricingData.bookingRefNo || "",
        checkInDate: searchData.checkInDate,
        checkOutDate: searchData.checkOutDate,
        supplierParameter: pricingRoom.supplierParameter || "",
        currency: pricingData.currency || "INR",
        customMarkUp: manualMarkup,
        totalPrice: grandTotal,
        roomId: pricingRoom.roomId || "",
        rateId: pricingRoom.rateId || "",
        roomGuests: roomGuests,
        contactInfo: {
          email: contactEmail,
          phone: contactPhone,
          dialCode: dialCode,
        },
        isHold: true,
        key: pricingData?.data?.key || "",
        policyType: pricingData.policyType || "",
        policyReason: pricingData.policyReason || "",
        bookingFee: bookingFee,
        managementFee: managementFee,
        managementFeeTax: managementFeeTax,
        markUp: apiMarkup,
        reportingManager: corporateInfo?.reportingManager || "",
        approvelName: corporateInfo?.reportingManager || "",
        gstinfo: {
          gstNumber: gstNumber,
          name: gstName,
          email: gstEmail,
          phone: gstPhone,
          address: gstAddress,
        },
        CreatedBy: userName,
        corporateID: selectedCorporateCode || localStorage.getItem("selectedCorporateCode") || corporateId || "",
      };

      // First save hotel details as requested
      await SaveHotelDetails({ ...approvalPayload, isHold: true });

      const holdResponse = await hotelApproval(approvalPayload);

      if (
        holdResponse &&
        (holdResponse.success ||
          holdResponse.confirmationNo ||
          holdResponse.status === 1)
      ) {
        const approvalResponse = await ApprovalRequest({
          referenceNo: pricingData?.bookingRefNo || "",
          key: pricingData?.data?.key || "",
          userId: selectedUserId,
          product: "Hotel",
          level1Approver: corporateInfo?.reportingManager || "",
          departmentHead: corporateInfo?.departmentHead || "",
        });

        if (approvalResponse?.success === true) {
          alert(
            approvalResponse?.message || "Approval request sent successfully!",
          );
          clearHotelSession();
          navigate("/hotel");
        } else {
          alert(
            approvalResponse?.message ||
              "Approval request failed. Please try again.",
          );
        }
      } else {
        alert(
          holdResponse?.message ||
            "Hotel approval hold failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Approval Request failed:", error);
      alert("An error occurred while submitting the approval request.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleHoldBooking = async () => {
    if (!pricingData || !pricingRoom) {
      alert("Pricing details not available. Please try again.");
      return;
    }

    setIsHoldingBooking(true);
    try {
      const selectedCorporateCode =
        localStorage.getItem("selectedCorporateCode") || corporateId || "";
      const selectedUserId =
        localStorage.getItem("selectedUserId") || userName || "";

      const apiTotalAmount =
        pricingData?.totalPrice || pricingRoom?.price || roomData?.price || 0;
      // const grandTotal = apiTotalAmount  + managementFee;

      let guestPointer = 0;
      const roomGuests = searchData.roomGuests.map((room, idx) => {
        const adultsCount = room.noOfAdults;
        const childCount = room.noOfChilds || 0;
        const totalGuestsInRoom = adultsCount + childCount;

        const roomGuestsList = travellerDetails
          .slice(guestPointer, guestPointer + totalGuestsInRoom)
          .map((t, gIdx) => ({
            title: t.title || "Mr",
            firstName: t.firstName,
            lastName: t.lastName,
            isLead: gIdx === 0 && idx === 0,
            pan: t.pan || "",
            passportNumber: t.passport || "",
            paxType: t.type === "Adult" ? "ADULT" : "CHILD",
          }));

        guestPointer += totalGuestsInRoom;

        return {
          roomNo: idx + 1,
          noOfAdults: adultsCount,
          noOfChilds: childCount,
          childAge: room.childAge || [],
          guests: roomGuestsList,
        };
      });

      const holdPayload = {
        corporateTraveler: selectedUserId,
        corporateCode: selectedCorporateCode,
        travelerType: searchData.travelType || "1",
        traceId: pricingData.traceId,
        hotelId: hotelData.hotelCode || pricingData.hotelId || "",
        bookingRefNo: pricingData.bookingRefNo || "",
        checkInDate: searchData.checkInDate,
        checkOutDate: searchData.checkOutDate,
        supplierParameter: pricingRoom.supplierParameter || "",
        currency: pricingData.currency || "INR",
        customMarkUp: manualMarkup,
        totalPrice: grandTotal.toFixed(2),
        roomId: pricingRoom.roomId || "",
        rateId: pricingRoom.rateId || "",
        roomGuests: roomGuests,
        contactInfo: {
          email: contactEmail,
          phone: contactPhone,
          dialCode: dialCode,
        },
        isHold: true,
        key: pricingData?.data?.key || "",
        policyType: pricingData.policyType || "",
        policyReason: pricingData.policyReason || "",
        bookingFee: bookingFee,
        managementFee: managementFee,
        managementFeeTax: managementFeeTax,
        reportingManager: corporateInfo?.reportingManager || "",
        gstinfo: {
          gstNumber: gstNumber,
          name: gstName,
          email: gstEmail,
          phone: gstPhone,
          address: gstAddress,
        },
        CreatedBy: userName,
      };

      // First save hotel details as requested
       await SaveHotelDetails({ ...holdPayload, isHold: true });
       //console.log(holdPayload)
      const response = await holdHotel(holdPayload);
      if (
        response &&
        (response.status === 1 || response.success || response.confirmationNo)
      ) {
        clearHotelSession();
        navigate("/hotel/thankyou?refId=" + response.bookingRefId);
      } else {
        alert(response?.message || "Booking hold failed. Please try again.");
      }
    } catch (error) {
      console.error("Hold booking failed:", error);
      alert("An error occurred while holding the booking.");
    } finally {
      setIsHoldingBooking(false);
    }
  };

  const handleSendPaymentLink = async () => {
    if (!contactEmail || !contactPhone) {
      alert("Please provide both email and phone number for the payment link.");
      return;
    }

    if (!pricingData || !pricingRoom) {
      alert("Pricing details not available. Please try again.");
      return;
    }

    setIsSendingPaymentLink(true);
    try {
      const selectedCorporateCode =
        localStorage.getItem("selectedCorporateCode") || corporateId || "";
      const selectedUserId =
        localStorage.getItem("selectedUserId") || userName || "";

      // Construct roomGuests structure according to new API requirement
      let guestPointer = 0;
      const roomGuests = searchData.roomGuests.map((room, idx) => {
        const adultsCount = room.noOfAdults;
        const childCount = room.noOfChilds || 0;
        const totalGuestsInRoom = adultsCount + childCount;

        const roomGuestsList = travellerDetails
          .slice(guestPointer, guestPointer + totalGuestsInRoom)
          .map((t, gIdx) => ({
            title: t.title || "Mr",
            firstName: t.firstName,
            lastName: t.lastName,
            isLead: gIdx === 0 && idx === 0,
            pan: t.pan || "",
            passportNumber: t.passport || "",
            paxType: t.type === "Adult" ? "ADULT" : "CHILD",
          }));

        guestPointer += totalGuestsInRoom;

        return {
          roomNo: idx + 1,
          noOfAdults: adultsCount,
          noOfChilds: childCount,
          childAge: room.childAge || [],
          guests: roomGuestsList,
        };
      });

      const approvalPayload = {
        corporateTraveler: selectedUserId,
        corporateCode: selectedCorporateCode,
        travelerType: searchData.travelType || "1",
        traceId: pricingData.traceId,
        hotelId: hotelData.hotelCode || pricingData.hotelId || "",
        bookingRefNo: pricingData.bookingRefNo || "",
        checkInDate: searchData.checkInDate,
        checkOutDate: searchData.checkOutDate,
        supplierParameter: pricingRoom.supplierParameter || "",
        currency: pricingData.currency || "INR",
        customMarkUp: manualMarkup,
        totalPrice: grandTotal.toFixed(2),
        roomId: pricingRoom.roomId || "",
        rateId: pricingRoom.rateId || "",
        roomGuests: roomGuests,
        contactInfo: {
          email: contactEmail,
          phone: contactPhone,
          dialCode: dialCode,
        },
        isHold: true,
        key: pricingData?.data?.key || "",
        policyType: pricingData.policyType || "",
        policyReason: pricingData.policyReason || "",
        bookingFee: bookingFee,
        managementFee: managementFee,
        managementFeeTax: managementFeeTax,
        markUp: apiMarkup,
        reportingManager: corporateInfo?.reportingManager || "",
        approvelName: corporateInfo?.reportingManager || "",
        gstinfo: {
          gstNumber: gstNumber,
          name: gstName,
          email: gstEmail,
          phone: gstPhone,
          address: gstAddress,
        },
        CreatedBy: userName,
      };

      // First save hotel details as requested
      await SaveHotelDetails({ ...approvalPayload, isHold: true });

      // Step 1: Call Hotel Approval endpoint to hold the booking
      const holdResponse = await hotelApproval(approvalPayload);

      if (
        holdResponse &&
        (holdResponse.success ||
          holdResponse.confirmationNo ||
          holdResponse.status === 1)
      ) {
        // Step 2: Now send the payment link
        const response = await SendPaymentLinkMail({
          referenceNo: pricingData?.bookingRefNo || "",
          product: "Hotel",
        });

        if (response && response.success) {
          alert("Payment link sent successfully!");
          clearHotelSession();
          navigate("/hotel");
        } else {
          alert(response?.message || "Payment link notification triggered.");
          clearHotelSession();
          navigate("/hotel");
        }
      } else {
        alert(
          holdResponse?.message ||
            "Hotel approval hold failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Send Payment Link failed:", error);
      alert("An error occurred while sending the payment link.");
    } finally {
      setIsSendingPaymentLink(false);
    }
  };

  const emulateCorporateCode = localStorage.getItem("selectedCorporateCode");
  const paymentButton =
    (roles === "TMC Operations" || roles === "TMC Manager") &&
    emulateCorporateCode != null ? (
      <>
        <button
          className="btn btn-warning px-5 rounded-pill me-3"
          style={{ height: "45px", fontWeight: "600", color: "white" }}
          onClick={handleSendPaymentLink}
          disabled={isSendingPaymentLink || isProcessingPayment}
        >
          {isSendingPaymentLink ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Sending...
            </>
          ) : (
            "Send Payment Link"
          )}
        </button>
        <button
          className={
            isAgreed
              ? "btn-choose-room px-5"
              : "btn btn-secondary px-5 rounded-pill"
          }
          style={{
            height: "45px",
            fontWeight: "600",
            cursor: isAgreed ? "pointer" : "not-allowed",
          }}
          disabled={isProcessingPayment || isSendingPaymentLink || !isAgreed}
          onClick={handlePayment}
        >
          {isProcessingPayment ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Processing...
            </>
          ) : (
            "Pay & Book"
          )}
        </button>
      </>
    ) : (
      <button
        className={
          isAgreed
            ? "btn-choose-room px-5"
            : "btn btn-secondary px-5 rounded-pill"
        }
        style={{
          height: "45px",
          fontWeight: "600",
          cursor: isAgreed ? "pointer" : "not-allowed",
        }}
        disabled={isProcessingPayment || isSendingPaymentLink || !isAgreed}
        onClick={handlePayment}
      >
        {isProcessingPayment ? (
          <>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Processing...
          </>
        ) : (
          "Pay & Book"
        )}
      </button>
    );

  const ApprovalRequestButton =
    isCorporateTraveller && isBookingApprovalRequired ? (
      <button
        className="btn-choose-room px-5"
        onClick={SubmitApprovalRequest}
        disabled={isProcessingPayment}
      >
        {isProcessingPayment ? (
            <span className="spinner-border spinner-border-sm me-2"></span>
        ) : (
          "Request For Approval"
        )}
      </button>
    ) : (
      <button
        className="btn-choose-room px-5"
        onClick={() => {
          setActiveStep(3);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      >
        Continue
      </button>
    );

  if (loading || !hotelData || !searchData) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        {/* <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div> */}
        <p class="text-center load"><span class="loader"></span></p>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="container mt-5 pt-5">
          <div
            className="alert alert-danger d-flex align-items-center p-5 shadow-sm"
            role="alert"
          >
            <i className="fas fa-exclamation-triangle fs-1 me-4"></i>
            <div>
              <h4 className="alert-heading fw-bold mb-2">Pricing Error</h4>
              <p className="mb-0 fs-6">{error}</p>
              <button
                className="btn-choose-room mt-4"
                onClick={() => navigate(-1)}
              >
                Go Back to Search
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Formatting helpers
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      day: d.getDate(),
      month: d.toLocaleString("default", { month: "short" }),
      weekday: d.toLocaleString("default", { weekday: "short" }),
    };
  };

  const inDate = formatDate(searchData.checkInDate);
  const outDate = formatDate(searchData.checkOutDate);

  // Extract pricing details
  const pricingRoom = pricingData?.data?.rooms?.[0] || roomData;
  const priceDetails = pricingRoom?.priceDetails;

  // Extract Markup and Booking Fee from API - check all possible paths
  const apiMarkup =
    pricingData?.markupInfo?.markupAmount ||
    pricingData?.data?.rooms?.[0]?.priceDetails?.markup ||
    roomData?.priceDetails?.markup ||
    0;

  const apiBookingFee =
    pricingData?.bookingFeeInfo?.bookingFeeAmount ||
    pricingData?.data?.rooms?.[0]?.priceDetails?.bookingFee ||
    roomData?.priceDetails?.bookingFee ||
    0;

  const apiManagementFee =
    pricingData?.data?.rooms?.[0]?.priceDetails?.managementFee ||
    roomData?.priceDetails?.managementFee ||
    0;

  const apiManagementFeeTax =
    pricingData?.data?.rooms?.[0]?.priceDetails?.managementFeeTax ||
    roomData?.priceDetails?.managementFeeTax ||
    0;

  const apiTaxAmount =
    pricingData?.data?.rooms?.[0]?.priceDetails?.totalTax ||
    roomData?.priceDetails?.totalTax ||
    0;

  const apiGstAmount =
    pricingData?.data?.rooms?.[0]?.priceDetails?.gstAmount ||
    roomData?.priceDetails?.gstAmount ||
    0;

  // Calculate Net Base Price (Total Base - API Markup)
  const netBasePrice =
    (priceDetails?.totalBasePrice ||
      pricingRoom?.price ||
      roomData.price ||
      0) - apiMarkup;

  // Final display values
  const displayBasePrice = Math.round(netBasePrice + manualMarkup);
  const totalMarkup = manualMarkup;
  const bookingFee = apiBookingFee;
  const managementFee = apiManagementFee;
  const managementFeeTax = apiManagementFeeTax;
  const displayTaxesAndFees =
    Math.round(apiTaxAmount + managementFee + managementFeeTax);
  const grandTotal = displayBasePrice + displayTaxesAndFees ;

  const nights = (() => {
    const start = new Date(searchData.checkInDate);
    const end = new Date(searchData.checkOutDate);
    const diff = end.getTime() - start.getTime();
    return Math.ceil(diff / (1000 * 3600 * 24));
  })();
  return (
    <>
      {(isProcessingPayment || isHoldingBooking) && (
      
        <p className="text-center load">
          <span className="loader"></span>
        </p>
    
      )}
      <style>{`
        .Booking_flight .text-danger,
        .modal .text-danger {
          color: #ff0000 !important;
        }
      `}</style>
      <Header></Header>
      <section className="Booking_flight">
        <div className="container_box">
          <div className="row">
            <div className="col-12 col-md-9">
              {/* Step 1 & 2 Share the same Hotel Summary Card structure as per pic */}
              {(activeStep === 1 || activeStep === 2) && (
                <div className="box_shadow mb-4">
               
                  <div className="d-flex flex-wrap justify-content-start align-items-center gap-3 mb-4 booking-wizard-tabs">
                    <button
                      className={`btn ${activeStep === 1 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      onClick={() => activeStep > 1 && setActiveStep(1)}
                      style={{
                        borderRadius: "5px",
                        padding: "10px 25px",
                        fontWeight: "600",
                        background: activeStep === 1 ? "#110942" : "#fff",
                        color: activeStep === 1 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                      }}
                    >
                      TRAVELLERS' DETAILS
                    </button>
                    <button
                      className={`btn ${activeStep === 2 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      onClick={() => activeStep > 1 && setActiveStep(2)}
                      style={{
                        borderRadius: "5px",
                        padding: "10px 25px",
                        fontWeight: "600",
                        background: activeStep === 2 ? "#110942" : "#fff",
                        color: activeStep === 2 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                      }}
                    >
                      CONFIRM BOOKING
                    </button>
                    <button
                      className={`btn ${activeStep === 3 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      style={{
                        borderRadius: "5px",
                        padding: "8px 15px",
                        fontWeight: "600",
                        background: activeStep === 3 ? "#110942" : "#fff",
                        color: activeStep === 3 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                        opacity: activeStep < 3 ? 0.7 : 1,
                      }}
                    >
                      PAYMENT
                    </button>
                  </div>
                  <div className="row mamage_depo">
                    <div className="col-12 col-lg-6">
                      <h3 className="sub_headings" style={{ color: "#110942" }}>
                        {activeStep === 1
                          ? "Review Your Booking"
                          : "Confirm Booking"}
                      </h3>
                    </div>
                    <div className="col-12 col-lg-6 text-end">
                      <a
                        href="/hotel/hoteldetail"
                        className="click_back bg-light rounded-circle p-2 shadow-sm"
                      >
                        <i className="fa fa-arrow-left"></i>
                      </a>
                    </div>
                    <div className="col-12 col-md-12">
                      <div className="box-content">
                        <div className="col-12 col-md-12">
                          <h3
                            className="hotelNam fw-bold"
                            style={{ color: "#8B0000" }}
                          >
                            {hotelData.hotelName}
                          </h3>
                          <p className="p_detls text-muted small">
                            {hotelData.address}
                          </p>
                          <div className="row align-items-end">
                            <div className="col-12 col-md-7">
                              <div className="col-12 col-md-12 d-flex flex-wrap holding_booking gap-3 mt-3">
                                <div className="box_date">
                                  <label className="text-danger small fw-bold d-block text-center mb-1">
                                    Check In
                                  </label>
                                  <div
                                    className="date d-flex border rounded shadow-sm overflow-hidden bg-white"
                                    style={{ minWidth: "180px" }}
                                  >
                                    <div className="px-2 py-2 border-end text-center flex-grow-1">
                                      <label
                                        className="month d-block fw-bold"
                                        style={{
                                          color: "#8B0000",
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        {inDate.month}
                                      </label>
                                    </div>
                                    <div className="px-3 py-2 border-end text-center flex-grow-1">
                                      <label
                                        className="date fw-bold"
                                        style={{
                                          color: "#8B0000",
                                          fontSize: "1.1rem",
                                        }}
                                      >
                                        {inDate.day}
                                      </label>
                                    </div>
                                    <div
                                      className="bg-navy px-3 py-2 d-flex align-items-center justify-content-center fw-bold flex-grow-1"
                                      style={{ backgroundColor: "#110942" }}
                                    >
                                      <span style={{ color: "#FFD700" }}>
                                        {inDate.weekday}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="box_date">
                                  <label className="text-danger small fw-bold d-block text-center mb-1">
                                    Check Out
                                  </label>
                                  <div
                                    className="date d-flex border rounded shadow-sm overflow-hidden bg-white"
                                    style={{ minWidth: "180px" }}
                                  >
                                    <div className="px-2 py-2 border-end text-center flex-grow-1">
                                      <label
                                        className="month d-block fw-bold"
                                        style={{
                                          color: "#8B0000",
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        {outDate.month}
                                      </label>
                                    </div>
                                    <div className="px-3 py-2 border-end text-center flex-grow-1">
                                      <label
                                        className="date fw-bold"
                                        style={{
                                          color: "#8B0000",
                                          fontSize: "1.1rem",
                                        }}
                                      >
                                        {outDate.day}
                                      </label>
                                    </div>
                                    <div
                                      className="bg-navy px-3 py-2 d-flex align-items-center justify-content-center fw-bold flex-grow-1"
                                      style={{ backgroundColor: "#110942" }}
                                    >
                                      <span style={{ color: "#FFD700" }}>
                                        {outDate.weekday}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-5 text-start text-md-end mt-3 mt-md-0">
                              <p className="chld_p small text-muted">
                                Adults:{" "}
                                {searchData.roomGuests.reduce(
                                  (acc, r) => acc + r.noOfAdults,
                                  0,
                                )}{" "}
                                | Children:{" "}
                                {searchData.roomGuests.reduce(
                                  (acc, r) => acc + r.noOfChilds,
                                  0,
                                )}{" "}
                                | Room : {searchData.roomGuests.length} |
                                Nights: {nights}
                              </p>
                              <h3 className="hotelNa fw-bold fs-6 text-dark">
                                {pricingRoom?.roomDetails?.[0]?.roomName ||
                                  roomData.roomName}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-12 mt-4 pt-3 border-top">
                          <h3 className="hotelN fs-6 fw-bold">
                            {" "}
                            Tariff Inclusions
                          </h3>
                          <p className="tarrif_rt small">
                            (
                            {pricingRoom?.inclusions?.length > 0
                              ? pricingRoom.inclusions.join(", ")
                              : "Free valet parking, Free self parking"}
                            )
                          </p>
                        </div>

                        {/* Cancellation Policy - Show in Step 1 (Review Your Booking) */}
                        {
                          pricingRoom?.cancellationPolicy &&
                          pricingRoom.cancellationPolicy.length > 0 && (
                            <div className="col-12 col-md-12 mt-4 pt-3 border-top">
                              <h3 className="hotelN fs-6 fw-bold">
                                {" "}
                                Cancellation Policy
                              </h3>
                              <div className="d-flex gap-2 mt-2">
                                {pricingRoom.cancellationPolicy.some(
                                  (p) => p.penaltyAmount === 0,
                                ) ? (
                                  <span className="badge" style={{background:"#11a670",color:"#fff"}}>
                                    Refundable
                                  </span>
                                ) : (
                                  <span className="badge" style={{background:"#FF0000",color:"#fff"}}>
                                    Non-Refundable
                                  </span>
                                )}
                                <button
                                  className="btn btn-link p-0 small fw-bold text-decoration-none"
                                  style={{ fontSize: "12px", color: "#295eff" }}
                                  onClick={() => setShowCancellationModal(true)}
                                >
                                  View Policy
                                </button>
                              </div>
                            </div>
                          )}

                        {/* Hotel Info - Show in Step 2 (Confirm Booking) */}
                        {/* {activeStep === 2 && (
                          <div className="col-12 col-md-12 mt-4 pt-3 border-top">
                            <h3 className="hotelN fs-6 fw-bold">Hotel Info</h3>
                            <ul className="small mb-0 mt-2">
                              <li>Early check out will attract full cancellation charge unless otherwise specified.</li>
                              <li>Default Check In Time : 12:00:00</li>
                              <li>Default Check Out Time : 11:00:00</li>
                            </ul>
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeStep === 1 && (
                <div className="box_shadow">
                  <h3 className="sub_headings" style={{ color: "#110942" }}>
                    Enter Traveller Details
                  </h3>
                  <div className="Traveller">
                    <h5>Contact Details</h5>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="form-group">
                          <label className="required">Email Address</label>
                          <input
                            className="form-control form-control-solid"
                            name=""
                            value={contactEmail}
                            onChange={(e) =>
                              handleContactEmailChange(e.target.value)
                            }
                            type="text"
                            placeholder="Email Address"
                          />
                          {fieldErrors.contactEmail && (
                            <span
                              className="text-danger x-small fw-bold"
                              style={{ fontSize: "11px" }}
                            >
                              {fieldErrors.contactEmail}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-5">
                        <div className="form-group">
                          <label className="required">Contact Number</label>
                          <div className="d-flex">
                            <div className="selct">
                              <select
                                className="form-select form-select-solid"
                                value={dialCode ? (dialCode.startsWith("+") ? dialCode : `+${dialCode}`) : "+91"}
                                onChange={(e) => setDialCode(e.target.value)}
                              >
                                <option value="+91">India (+91)</option>
                                <option value="+93">Afghanistan (+93)</option>
                                <option value="+355">Albania (+355)</option>
                                <option value="+213">Algeria (+213)</option>
                                <option value="+1 684">
                                  American Samoa (+1 684)
                                </option>
                                <option value="+376">Andorra (+376)</option>
                                <option value="+244">Angola (+244)</option>
                                <option value="+1 264">
                                  Anguilla (+1 264)
                                </option>
                                <option value="+672">Antarctica (+672)</option>
                                <option value="+1 268">
                                  Antigua and Barbuda (+1 268)
                                </option>
                                <option value="+54">Argentina (+54)</option>
                                <option value="+374">Armenia (+374)</option>
                                <option value="+297">Aruba (+297)</option>
                                <option value="+61">Australia (+61)</option>
                                <option value="+43">Austria (+43)</option>
                                <option value="+994">Azerbaijan (+994)</option>
                                <option value="+1 242">Bahamas (+1 242)</option>
                                <option value="+973">Bahrain (+973)</option>
                                <option value="+880">Bangladesh (+880)</option>
                                <option value="+1 246">
                                  Barbados (+1 246)
                                </option>
                                <option value="+375">Belarus (+375)</option>
                                <option value="+32">Belgium (+32)</option>
                                <option value="+501">Belize (+501)</option>
                                <option value="+229">Benin (+229)</option>
                                <option value="+1 441">Bermuda (+1 441)</option>
                                <option value="+975">Bhutan (+975)</option>
                                <option value="+591">Bolivia (+591)</option>
                                <option value="+387">
                                  Bosnia and Herzegovina (+387)
                                </option>
                                <option value="+267">Botswana (+267)</option>
                                <option value="+55">Brazil (+55)</option>
                                <option value="+1 284">
                                  British Virgin Islands (+1 284)
                                </option>
                                <option value="+673">Brunei (+673)</option>
                                <option value="+359">Bulgaria (+359)</option>
                                <option value="+226">
                                  Burkina Faso (+226)
                                </option>
                                <option value="+95">
                                  Burma (Myanmar) (+95)
                                </option>
                                <option value="+257">Burundi (+257)</option>
                                <option value="+855">Cambodia (+855)</option>
                                <option value="+237">Cameroon (+237)</option>
                                <option value="+1">Canada (+1)</option>
                                <option value="+238">Cape Verde (+238)</option>
                                <option value="+1 345">
                                  Cayman Islands (+1 345)
                                </option>
                                <option value="+236">
                                  Central African Republic (+236)
                                </option>
                                <option value="+235">Chad (+235)</option>
                                <option value="+56">Chile (+56)</option>
                                <option value="+86">China (+86)</option>
                                <option value="+61">
                                  Christmas Island (+61)
                                </option>
                                <option value="+61">
                                  Cocos (Keeling) Islands (+61)
                                </option>
                                <option value="+57">Colombia (+57)</option>
                                <option value="+269">Comoros (+269)</option>
                                <option value="+682">
                                  Cook Islands (+682)
                                </option>
                                <option value="+506">Costa Rica (+506)</option>
                                <option value="+385">Croatia (+385)</option>
                                <option value="+53">Cuba (+53)</option>
                                <option value="+357">Cyprus (+357)</option>
                                <option value="+420">
                                  Czech Republic (+420)
                                </option>
                                <option value="+243">
                                  Democratic Republic of the Congo (+243)
                                </option>
                                <option value="+45">Denmark (+45)</option>
                                <option value="+253">Djibouti (+253)</option>
                                <option value="+1 767">
                                  Dominica (+1 767)
                                </option>
                                <option value="+1 809">
                                  Dominican Republic (+1 809)
                                </option>
                                <option value="+593">Ecuador (+593)</option>
                                <option value="+20">Egypt (+20)</option>
                                <option value="+503">El Salvador (+503)</option>
                                <option value="+240">
                                  Equatorial Guinea (+240)
                                </option>
                                <option value="+291">Eritrea (+291)</option>
                                <option value="+372">Estonia (+372)</option>
                                <option value="+251">Ethiopia (+251)</option>
                                <option value="+500">
                                  Falkland Islands (+500)
                                </option>
                                <option value="+298">
                                  Faroe Islands (+298)
                                </option>
                                <option value="+679">Fiji (+679)</option>
                                <option value="+358">Finland (+358)</option>
                                <option value="+33">France (+33)</option>
                                <option value="+689">
                                  French Polynesia (+689)
                                </option>
                                <option value="+241">Gabon (+241)</option>
                                <option value="+220">Gambia (+220)</option>
                                <option value="+970">Gaza Strip (+970)</option>
                                <option value="+995">Georgia (+995)</option>
                                <option value="+49">Germany (+49)</option>
                                <option value="+233">Ghana (+233)</option>
                                <option value="+350">Gibraltar (+350)</option>
                                <option value="+30">Greece (+30)</option>
                                <option value="+299">Greenland (+299)</option>
                                <option value="+1 473">Grenada (+1 473)</option>
                                <option value="+1 671">Guam (+1 671)</option>
                                <option value="+502">Guatemala (+502)</option>
                                <option value="+224">Guinea (+224)</option>
                                <option value="+245">
                                  Guinea-Bissau (+245)
                                </option>
                                <option value="+592">Guyana (+592)</option>
                                <option value="+509">Haiti (+509)</option>
                                <option value="+39">
                                  Holy See (Vatican City) (+39)
                                </option>
                                <option value="+504">Honduras (+504)</option>
                                <option value="+852">Hong Kong (+852)</option>
                                <option value="+36">Hungary (+36)</option>
                                <option value="+354">Iceland (+354)</option>
                                {/* <option value="91">India (+91)</option> */}
                                <option value="+62">Indonesia (+62)</option>
                                <option value="+98">Iran (+98)</option>
                                <option value="+964">Iraq (+964)</option>
                                <option value="+353">Ireland (+353)</option>
                                <option value="+44">Isle of Man (+44)</option>
                                <option value="+972">Israel (+972)</option>
                                <option value="+39">Italy (+39)</option>
                                <option value="+225">Ivory Coast (+225)</option>
                                <option value="+1 876">Jamaica (+1 876)</option>
                                <option value="+81">Japan (+81)</option>
                                <option value="+962">Jordan (+962)</option>
                                <option value="+7">Kazakhstan (+7)</option>
                                <option value="+254">Kenya (+254)</option>
                                <option value="+686">Kiribati (+686)</option>
                                <option value="+381">Kosovo (+381)</option>
                                <option value="+965">Kuwait (+965)</option>
                                <option value="+996">Kyrgyzstan (+996)</option>
                                <option value="+856">Laos (+856)</option>
                                <option value="+371">Latvia (+371)</option>
                                <option value="+961">Lebanon (+961)</option>
                                <option value="+266">Lesotho (+266)</option>
                                <option value="+231">Liberia (+231)</option>
                                <option value="+218">Libya (+218)</option>
                                <option value="+423">
                                  Liechtenstein (+423)
                                </option>
                                <option value="+370">Lithuania (+370)</option>
                                <option value="+352">Luxembourg (+352)</option>
                                <option value="+853">Macau (+853)</option>
                                <option value="+389">Macedonia (+389)</option>
                                <option value="+261">Madagascar (+261)</option>
                                <option value="+265">Malawi (+265)</option>
                                <option value="+60">Malaysia (+60)</option>
                                <option value="+960">Maldives (+960)</option>
                                <option value="+223">Mali (+223)</option>
                                <option value="+356">Malta (+356)</option>
                                <option value="+692">
                                  Marshall Islands (+692)
                                </option>
                                <option value="+222">Mauritania (+222)</option>
                                <option value="+230">Mauritius (+230)</option>
                                <option value="+262">Mayotte (+262)</option>
                                <option value="+52">Mexico (+52)</option>
                                <option value="+691">Micronesia (+691)</option>
                                <option value="+373">Moldova (+373)</option>
                                <option value="+377">Monaco (+377)</option>
                                <option value="+976">Mongolia (+976)</option>
                                <option value="+382">Montenegro (+382)</option>
                                <option value="+1 664">
                                  Montserrat (+1 664)
                                </option>
                                <option value="+212">Morocco (+212)</option>
                                <option value="+258">Mozambique (+258)</option>
                                <option value="+264">Namibia (+264)</option>
                                <option value="+674">Nauru (+674)</option>
                                <option value="+977">Nepal (+977)</option>
                                <option value="+31">Netherlands (+31)</option>
                                <option value="+599">
                                  Netherlands Antilles (+599)
                                </option>
                                <option value="+687">
                                  New Caledonia (+687)
                                </option>
                                <option value="+64">New Zealand (+64)</option>
                                <option value="+505">Nicaragua (+505)</option>
                                <option value="+227">Niger (+227)</option>
                                <option value="+234">Nigeria (+234)</option>
                                <option value="+683">Niue (+683)</option>
                                <option value="+672">
                                  Norfolk Island (+672)
                                </option>
                                <option value="+850">North Korea (+850)</option>
                                <option value="+1 670">
                                  Northern Mariana Islands (+1 670)
                                </option>
                                <option value="+47">Norway (+47)</option>
                                <option value="+968">Oman (+968)</option>
                                <option value="+92">Pakistan (+92)</option>
                                <option value="+680">Palau (+680)</option>
                                <option value="+507">Panama (+507)</option>
                                <option value="+675">
                                  Papua New Guinea (+675)
                                </option>
                                <option value="+595">Paraguay (+595)</option>
                                <option value="+51">Peru (+51)</option>
                                <option value="+63">Philippines (+63)</option>
                                <option value="+870">
                                  Pitcairn Islands (+870)
                                </option>
                                <option value="+48">Poland (+48)</option>
                                <option value="+351">Portugal (+351)</option>
                                <option value="+1">Puerto Rico (+1)</option>
                                <option value="+974">Qatar (+974)</option>
                                <option value="+242">
                                  Republic of the Congo (+242)
                                </option>
                                <option value="+40">Romania (+40)</option>
                                <option value="+7">Russia (+7)</option>
                                <option value="+250">Rwanda (+250)</option>
                                <option value="+590">
                                  Saint Barthelemy (+590)
                                </option>
                                <option value="+290">
                                  Saint Helena (+290)
                                </option>
                                <option value="+1 869">
                                  Saint Kitts and Nevis (+1 869)
                                </option>
                                <option value="+1 758">
                                  Saint Lucia (+1 758)
                                </option>
                                <option value="+1 599">
                                  Saint Martin (+1 599)
                                </option>
                                <option value="+508">
                                  Saint Pierre and Miquelon (+508)
                                </option>
                                <option value="+1 784">
                                  Saint Vincent and the Grenadines (+1 784)
                                </option>
                                <option value="+685">Samoa (+685)</option>
                                <option value="+378">San Marino (+378)</option>
                                <option value="+239">
                                  Sao Tome and Principe (+239)
                                </option>
                                <option value="+966">
                                  Saudi Arabia (+966)
                                </option>
                                <option value="+221">Senegal (+221)</option>
                                <option value="+381">Serbia (+381)</option>
                                <option value="+248">Seychelles (+248)</option>
                                <option value="+232">
                                  Sierra Leone (+232)
                                </option>
                                <option value="+65">Singapore (+65)</option>
                                <option value="+421">Slovakia (+421)</option>
                                <option value="+386">Slovenia (+386)</option>
                                <option value="+677">
                                  Solomon Islands (+677)
                                </option>
                                <option value="+252">Somalia (+252)</option>
                                <option value="+27">South Africa (+27)</option>
                                <option value="+82">South Korea (+82)</option>
                                <option value="+34">Spain (+34)</option>
                                <option value="+94">Sri Lanka (+94)</option>
                                <option value="+249">Sudan (+249)</option>
                                <option value="+597">Suriname (+597)</option>
                                <option value="+268">Swaziland (+268)</option>
                                <option value="+46">Sweden (+46)</option>
                                <option value="+41">Switzerland (+41)</option>
                                <option value="+963">Syria (+963)</option>
                                <option value="+886">Taiwan (+886)</option>
                                <option value="+992">Tajikistan (+992)</option>
                                <option value="+255">Tanzania (+255)</option>
                                <option value="+66">Thailand (+66)</option>
                                <option value="+670">Timor-Leste (+670)</option>
                                <option value="+228">Togo (+228)</option>
                                <option value="+690">Tokelau (+690)</option>
                                <option value="+676">Tonga (+676)</option>
                                <option value="+1 868">
                                  Trinidad and Tobago (+1 868)
                                </option>
                                <option value="+216">Tunisia (+216)</option>
                                <option value="+90">Turkey (+90)</option>
                                <option value="+993">
                                  Turkmenistan (+993)
                                </option>
                                <option value="+1 649">
                                  Turks and Caicos Islands (+1 649)
                                </option>
                                <option value="+688">Tuvalu (+688)</option>
                                <option value="+256">Uganda (+256)</option>
                                <option value="+380">Ukraine (+380)</option>
                                <option value="+44">
                                  United Kingdom (+44)
                                </option>
                                <option value="+1">United States (+1)</option>
                                <option value="+598">Uruguay (+598)</option>
                                <option value="+1 340">
                                  US Virgin Islands (+1 340)
                                </option>
                                <option value="+998">Uzbekistan (+998)</option>
                                <option value="+678">Vanuatu (+678)</option>
                                <option value="+58">Venezuela (+58)</option>
                                <option value="+84">Vietnam (+84)</option>
                                <option value="+681">
                                  Wallis and Futuna (+681)
                                </option>
                                <option value="+970">West Bank (+970)</option>
                                <option value="+967">Yemen (+967)</option>
                                <option value="+260">Zambia (+260)</option>
                                <option value="+263">Zimbabwe (+263)</option>
                              </select>
                            </div>
                            <div className="inpt w-100 ms-2">
                              <input
                                className="form-control form-control-solid"
                                name=""
                                type="text"
                                placeholder="Phone number"
                                value={contactPhone}
                                onChange={(e) =>
                                  handleContactPhoneChange(e.target.value)
                                }
                              />
                              {fieldErrors.contactPhone && (
                                <span
                                  className="text-danger x-small fw-bold"
                                  style={{ fontSize: "11px" }}
                                >
                                  {fieldErrors.contactPhone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-12">
                        <p className="small text-muted">
                          Your booking details will sent to this email address
                          and mobile number.
                        </p>
                      </div>
                    </div>

                    <h5 className="mt-4">Traveller Information</h5>
                    <p className="note p-2 bg-light rounded small border">
                      <span className="fw-bold text-danger">
                        Important Note :
                      </span>{" "}
                      Please ensure that the name of the passengers on the
                      travel documents is the same on their government issued
                      identity proof.
                    </p>

                    {(() => {
                      let guestPointer = 0;
                      return searchData?.roomGuests?.map((room, roomIdx) => {
                        const adultsCount = room.noOfAdults;
                        const childCount = room.noOfChilds || 0;
                        const totalGuestsInRoom = adultsCount + childCount;
                        const currentRoomPointer = guestPointer;
                        guestPointer += totalGuestsInRoom;

                        return (
                          <div key={roomIdx} className="mb-5">
                            <div
                              className="d-flex align-items-center mb-3 bg-light p-3 rounded border-start border-4 border-warning"
                              style={{ background: "#fefdf2" }}
                            >
                              <i className="fas fa-bed me-2 text-warning"></i>
                              <h6 className="mb-0 fw-bold text-dark">
                                Room {roomIdx + 1} -{" "}
                                <span className="text-muted small">
                                  {adultsCount} Adult(s)
                                  {childCount > 0
                                    ? `, ${childCount} Child(ren)`
                                    : ""}
                                </span>
                              </h6>
                            </div>

                            {travellerDetails
                              .slice(
                                currentRoomPointer,
                                currentRoomPointer + totalGuestsInRoom,
                              )
                              .map((traveller, guestIdx) => {
                                const absoluteIdx =
                                  currentRoomPointer + guestIdx;
                                const isChild = traveller.type === "Child";
                                const pricingRoom =
                                  pricingData?.data?.rooms?.[0];

                                return (
                                  <div
                                    className={`row ${isChild ? "Child" : "Adult"} g-3 mb-4 pb-4 border-bottom`}
                                    key={`${traveller.type}-${absoluteIdx}`}
                                  >
                                    <div className="col-12 col-md-12">
                                      <h4
                                        className={`${traveller.type} fw-bold text-uppercase`}
                                        style={{
                                          color: "#110942",
                                          fontSize: "0.8rem",
                                          letterSpacing: "0.5px",
                                        }}
                                      >
                                        <i
                                          className={`fas ${isChild ? "fa-child" : "fa-user"} me-2`}
                                        ></i>
                                        {isChild ? "CHILD" : "ADULT"}{" "}
                                        {guestIdx + 1}
                                      </h4>
                                    </div>
                                    <div className="col-12 col-md-4">
                                      <div className="row g-2">
                                        <div className="col-4">
                                          <label className="small fw-bold">
                                            Title <span className="text-danger">*</span>
                                          </label>
                                          <select
                                            className="form-select form-select-solid mt-1"
                                            value={traveller.title}
                                            onChange={(e) =>
                                              handleTravellerChange(
                                                absoluteIdx,
                                                "title",
                                                e.target.value,
                                              )
                                            }
                                          >
                                            {!isChild ? (
                                              <>
                                                <option value="Mr">Mr</option>
                                                <option value="Mrs">Mrs</option>
                                                <option value="Ms">Ms</option>
                                                {/* <option value="Miss">
                                                  Miss
                                                </option> */}
                                              </>
                                            ) : (
                                              <>
                                                <option value="Master">
                                                  Master
                                                </option>
                                                {/* <option value="Miss">
                                                  Miss
                                                </option> */}
                                              </>
                                            )}
                                          </select>
                                        </div>
                                        <div className="col-8">
                                          <label className="small fw-bold">
                                            First Name <span className="text-danger">*</span>
                                          </label>
                                          <input
                                            className="form-control form-control-solid mt-1"
                                            type="text"
                                            placeholder="First Name"
                                            value={
                                              travellerDetails[absoluteIdx]
                                                ?.firstName || ""
                                            }
                                            onChange={(e) =>
                                              handleTravellerChange(
                                                absoluteIdx,
                                                "firstName",
                                                e.target.value,
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-4">
                                      <label className="small fw-bold">
                                        Last Name <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        className="form-control form-control-solid mt-1"
                                        type="text"
                                        placeholder="Last Name"
                                        value={
                                          travellerDetails[absoluteIdx]
                                            ?.lastName || ""
                                        }
                                        onChange={(e) =>
                                          handleTravellerChange(
                                            absoluteIdx,
                                            "lastName",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>

                                    {/* Age Field (Replacing DOB for child only) */}
                                    {isChild && (
                                      <div className="col-12 col-md-4">
                                        <label className="small fw-bold">
                                          Age <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          className="form-select form-select-solid mt-1"
                                          disabled={isChild}
                                          value={
                                            travellerDetails[absoluteIdx]?.age ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleTravellerChange(
                                              absoluteIdx,
                                              "age",
                                              e.target.value,
                                            )
                                          }
                                          style={
                                            isChild
                                              ? { backgroundColor: "#e9ecef" }
                                              : {}
                                          }
                                        >
                                          {isChild ? (
                                            [...Array(17).keys()].map((n) => (
                                              <option
                                                key={n + 1}
                                                value={String(n + 1)}
                                              >
                                                {n + 1} Year
                                              </option>
                                            ))
                                          ) : (
                                            <>
                                              <option value="">Select Age</option>
                                              {[...Array(82).keys()].map((n) => (
                                                <option
                                                  key={n + 18}
                                                  value={String(n + 18)}
                                                >
                                                  {n + 18} Year
                                                </option>
                                              ))}
                                            </>
                                          )}
                                        </select>
                                        {fieldErrors[
                                          `traveller_${absoluteIdx}_age`
                                        ] && (
                                          <span className="text-danger x-small fw-bold">
                                            {
                                              fieldErrors[
                                                `traveller_${absoluteIdx}_age`
                                              ]
                                            }
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* PAN Field */}
                                    {(pricingRoom?.isPanRequired ||
                                      travellerDetails[absoluteIdx]?.pan) && (
                                      <div className="col-12 col-md-4">
                                        <label className="small fw-bold">
                                          PAN Number <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          className="form-control form-control-solid mt-1"
                                          type="text"
                                          placeholder="PAN Number"
                                          maxLength={10}
                                          value={
                                            travellerDetails[absoluteIdx]
                                              ?.pan || ""
                                          }
                                          onChange={(e) =>
                                            handleTravellerChange(
                                              absoluteIdx,
                                              "pan",
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {fieldErrors[
                                          `traveller_${absoluteIdx}_pan`
                                        ] && (
                                          <span
                                            className="text-danger x-small fw-bold"
                                            style={{ fontSize: "11px" }}
                                          >
                                            {
                                              fieldErrors[
                                                `traveller_${absoluteIdx}_pan`
                                              ]
                                            }
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Passport Field */}
                                    {(pricingRoom?.isPassportRequired ||
                                      travellerDetails[absoluteIdx]
                                        ?.passport) && (
                                      <div className="col-12 col-md-4">
                                        <label className="small fw-bold">
                                          Passport Number <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          className="form-control form-control-solid mt-1"
                                          type="text"
                                          placeholder="Passport Number"
                                          value={
                                            travellerDetails[absoluteIdx]
                                              ?.passport || ""
                                          }
                                          onChange={(e) =>
                                            handleTravellerChange(
                                              absoluteIdx,
                                              "passport",
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {fieldErrors[
                                          `traveller_${absoluteIdx}_passport`
                                        ] && (
                                          <span
                                            className="text-danger x-small fw-bold"
                                            style={{ fontSize: "11px" }}
                                          >
                                            {
                                              fieldErrors[
                                                `traveller_${absoluteIdx}_passport`
                                              ]
                                            }
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Create Corporate Traveller Profile */}
                                    {isTmc && (
                                      <div className="col-12 col-md-12 mt-3">
                                        <div className="form-check form-check-inline">
                                          <input
                                            className="form-check-input"
                                            id={`saveProfile_${absoluteIdx}`}
                                            type="checkbox"
                                            checked={
                                              travellerDetails[absoluteIdx]
                                                ?.saveInfo || false
                                            }
                                            onChange={(e) =>
                                              handleTravellerChange(
                                                absoluteIdx,
                                                "saveInfo",
                                                e.target.checked,
                                              )
                                            }
                                          />
                                          <label
                                            className="form-check-label small fw-bold"
                                            htmlFor={`saveProfile_${absoluteIdx}`}
                                          >
                                            Create Corporate Traveller Profile.
                                          </label>
                                        </div>
                                        {travellerDetails[absoluteIdx]
                                          ?.saveInfo && (
                                          <div className="mt-2">
                                            <label className="small fw-bold mb-1">
                                              Email ID / User Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                              type="text"
                                              className="form-control form-control-solid"
                                              placeholder="Enter email or username"
                                              style={{ maxWidth: "320px" }}
                                              value={
                                                travellerDetails[absoluteIdx]
                                                  ?.userName || ""
                                              }
                                              onChange={(e) =>
                                                handleTravellerChange(
                                                  absoluteIdx,
                                                  "userName",
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        );
                      });
                    })()}

                    {/* ── Billing Details (consistent with Flight module) ── */}
                    {isTmc && (
                      <div className="col-12 col-md-12 mt-4">
                        <h5 className="text-warning fw-bold fs-6 mb-3">
                          Billing Details
                        </h5>
                        <div className="row g-3">
                          <div className="col-12 col-md-3">
                            <label className="lbl">Billing Details <span className="text-danger">*</span></label>
                            <select
                              className="form-select form-select-solid fw-bold mt-1"
                              name="billTo"
                              value={organizationsDetails.billTo || ""}
                              onChange={(e) => {
                                setOrganizationsDetails((prev) => ({
                                  ...prev,
                                  billTo: e.target.value,
                                }));
                              }}
                              style={{ borderColor: "black" }}
                            >
                              <option value="">Select Billing Details</option>
                              {corporateList.map((corp) => (
                                <option
                                  key={corp.corporateCode || corp.value}
                                  value={corp.corporateCode || corp.value}
                                >
                                  {corp.corporateName || corp.label} (
                                  {corp.corporateCode})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* ── Select Traveller (matching Flight pattern) ── */}
                          {/* {isTmc && (
                          <div className="col-12 col-md-4">
                            <label className="small fw-bold">
                              Select Traveller
                            </label>
                            <select
                              className="form-select form-select-solid mt-1"
                              value={selectedTravellerId}
                              onChange={handleTravellerSelectionChange}
                            >
                              <option value="">Choose Traveller</option>
                              {travellerList.map((t) => (
                                <option
                                  key={t.id || t.userId || t.value}
                                  value={t.id || t.userId || t.value}
                                >
                                  {t.firstName} {t.lastName} (
                                  {t.userName || t.email})
                                </option>
                              ))}
                            </select>
                          </div>
                        )} */}
                        </div>

                        {/* ── Corporate Data Display (Balance from organizationsDetails) ── */}
                        {/* {organizationsDetails.companyId && (
                        <div className="mt-3 p-3 bg-light rounded border border-warning-subtle shadow-sm">
                          <div className="row align-items-center">
                            <div className="col-12 col-md-4 border-end">
                              <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Corporate Name</div>
                              <div className="fw-bold text-dark">{corporateInfo?.corporateName || "N/A"}</div>
                            </div>
                            <div className="col-12 col-md-4 border-end">
                              <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Available Balance</div>
                              <div className="fw-bold text-success fs-5">₹ {(organizationsDetails.cashBalance || corporateInfo?.availableBalance || 0).toLocaleString()}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="small text-muted text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>Credit Limit</div>
                              <div className="fw-bold text-dark">₹ {(corporateInfo?.creditLimit || 0).toLocaleString()}</div>
                            </div>
                          </div>
                        </div>
                      )} */}
                      </div>
                    )}
                    <div className="col-12 col-md-12 mt-4">
                      <h5 className="text-warning fw-bold fs-6 mb-3">
                        GST Details
                      </h5>
                      <div className="row g-3">
                        <div className="col-12 col-md-3">
                          <label className="small text-muted fw-bold">
                            GST Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-solid mt-1"
                            placeholder="GST Number"
                            value={gstNumber}
                            onChange={(e) => {
                              const val = e.target.value;
                              setGstNumber(val);
                              if (val.trim().length > 15) {
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  gstNumber: "GSTIN cannot exceed 15 characters.",
                                }));
                              } else {
                                setFieldErrors((prev) => {
                                  const newErr = { ...prev };
                                  delete newErr.gstNumber;
                                  return newErr;
                                });
                              }
                            }}
                          />
                          {fieldErrors.gstNumber && (
                            <span
                              className="text-danger x-small fw-bold"
                              style={{ fontSize: "11px" }}
                            >
                              {fieldErrors.gstNumber}
                            </span>
                          )}
                        </div>
                        <div className="col-12 col-md-3">
                          <label className="small text-muted fw-bold">
                            Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-solid mt-1"
                            placeholder="Name"
                            value={gstName}
                            onChange={(e) => setGstName(e.target.value)}
                          />
                        </div>
                        <div className="col-12 col-md-3">
                          <label className="small text-muted fw-bold">
                            Email <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control form-control-solid mt-1"
                            placeholder="Email"
                            value={gstEmail}
                            onChange={(e) => setGstEmail(e.target.value)}
                          />
                        </div>
                        <div className="col-12 col-md-3">
                          <label className="small text-muted fw-bold">
                            Phone <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-solid mt-1"
                            placeholder="Phone"
                            value={gstPhone}
                            onChange={(e) => setGstPhone(cleanPhoneNumber(e.target.value))}
                          />
                        </div>
                        <div className="col-12 col-md-12">
                          <label className="small text-muted fw-bold">
                            Address <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-solid mt-1"
                            placeholder="Address"
                            value={gstAddress}
                            onChange={(e) => setGstAddress(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-12 text-end mt-4">
                    <button
                      className="btn-choose-room"
                      onClick={async () => {
                        let currentTravellerDetails = [...travellerDetails];
                        let currentEmail = contactEmail;
                        let currentPhone = contactPhone;

                        if (isTmc && !selectedTravellerId) {
                          try {
                            const storedDetail =
                              localStorage.getItem("selectedDetail");
                            const parsed = (() => {
                              try {
                                return storedDetail
                                  ? JSON.parse(storedDetail)
                                  : {};
                              } catch (e) {
                                console.error(
                                  "Error parsing selectedDetail",
                                  e,
                                );
                                return {};
                              }
                            })();

                            let payloadData = {
                              email: parsed.email || "",
                              phoneCode: parsed.phoneCode || "91",
                              phoneNumber: parsed.phoneNumber || "",
                              corporateCode:
                                parsed.corporateCode ||
                                localStorage.getItem("selectedCorporateCode") ||
                                "",
                              userName:
                                parsed.userName ||
                                localStorage.getItem("selectedUserId") ||
                                "",
                              firstName: parsed.firstName || "",
                              lastName: parsed.lastName || "",
                              paxUserName:
                                parsed.paxUserName ||
                                parsed.userName ||
                                localStorage.getItem("selectedUserId") ||
                                "",
                            };

                            const tmcPayload = {
                              contactDetails: {
                                emailId: payloadData.email,
                                countryCode: payloadData.phoneCode,
                                mobile: payloadData.phoneNumber,
                              },
                              travellers: {
                                adults: [
                                  {
                                    title: "Mr",
                                    firstName: payloadData.firstName,
                                    lastName: payloadData.lastName,
                                    saveInfo: true,
                                    userName: payloadData.paxUserName,
                                  },
                                ],
                              },
                              corporateCode: payloadData.corporateCode,
                              userName: payloadData.userName,
                            };
                            console.log(tmcPayload);

                            // await CreateCorporateTravellerProfile(tmcPayload);

                            // Auto-fill the first adult with TMC details ONLY if data is present
                            if (currentTravellerDetails.length > 0) {
                              currentTravellerDetails[0] = {
                                ...currentTravellerDetails[0],
                                firstName:
                                  payloadData.firstName ||
                                  currentTravellerDetails[0].firstName,
                                lastName:
                                  payloadData.lastName ||
                                  currentTravellerDetails[0].lastName,
                                title: "Mr",
                                dob:
                                  currentTravellerDetails[0].dob ||
                                  "1990-01-01",
                              };
                            }
                            currentEmail = payloadData.email || currentEmail;
                            currentPhone =
                              payloadData.phoneNumber || currentPhone;

                            // Update state for UI
                            setTravellerDetails(currentTravellerDetails);
                            setContactEmail(currentEmail);
                            setContactPhone(currentPhone);
                          } catch (error) {
                            console.error("Error creating TMC profile:", error);
                          }
                        }

                        if (
                          validateStep1(
                            currentTravellerDetails,
                            currentEmail,
                            currentPhone,
                          )
                        ) {
                          setActiveStep(2);
                        }
                      }}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {activeStep === 2 && (
                <div className="box_shadow">
                  {/* <h3 className="sub_headings" style={{ color: "#110942" }}>
                    Cancellation Policy
                  </h3>
                  <div className="p-3 bg-light rounded border mb-4">
                    <p className="mb-2">
                      This is under cancellation booking and would attract
                      cancellation charges.
                    </p>
                    {/* <p className="fw-bold text-danger">
                      To avoid cancellation charges the booking must be
                      cancelled on or before :{" "}
                      {pricingRoom?.lastCancellationDate || "18 Sep 2025"}
                    </p> *
                    <button
                      className="btn btn-link p-0 small fw-bold text-decoration-none"
                      style={{
                        fontSize: "14px",
                        color: "#295eff",
                      }}
                      onClick={() => setShowCancellationModal(true)}
                    >
                      View Detailed Policy
                    </button>
                  </div> */}

                  <h3 className="sub_headings" style={{ color: "#110942" }}>
                    Contact Details
                  </h3>
                  <div className="p-3 bg-light rounded border mb-4">
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <label className="small text-muted">
                          Email Address
                        </label>
                        <p className="fw-bold mb-0">{contactEmail || "-"}</p>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="small text-muted">
                          Contact Number
                        </label>
                        <p className="fw-bold mb-0">
                          +91 {contactPhone || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <h3 className="sub_headings" style={{ color: "#110942" }}>
                    Traveller Details
                  </h3>
                  <div className="table-responsive mb-4 shadow-sm border rounded overflow-hidden">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr style={{ background: "#f8f9fa" }}>
                          <th
                            className="fw-bold small py-3 px-4"
                            style={{ width: "20%" }}
                          >
                            Type
                          </th>
                          <th className="fw-bold small py-3 px-4">Name</th>
                          <th className="fw-bold small py-3 px-4">
                            Age
                          </th>
                          {(pricingData?.data?.rooms?.[0]?.isPanRequired ||
                            travellerDetails.some((t) => t.pan)) && (
                            <th className="fw-bold small py-3 px-4">PAN</th>
                          )}
                          {(pricingData?.data?.rooms?.[0]?.isPassportRequired ||
                            travellerDetails.some((t) => t.passport)) && (
                            <th className="fw-bold small py-3 px-4">
                              Passport
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {travellerDetails.length > 0 ? (
                          (() => {
                            let guestPointer = 0;
                            return searchData?.roomGuests?.map(
                              (room, roomIdx) => {
                                const adultsCount = room.noOfAdults;
                                const childCount = room.noOfChilds || 0;
                                const totalGuestsInRoom =
                                  adultsCount + childCount;
                                const currentRoomPointer = guestPointer;
                                guestPointer += totalGuestsInRoom;

                                return (
                                  <React.Fragment key={roomIdx}>
                                    <tr className="bg-light">
                                      <td
                                        colSpan={5}
                                        className="fw-bold py-2 px-4 border-bottom"
                                        style={{
                                          color: "#110942",
                                          fontSize: "0.85rem",
                                          backgroundColor: "#f8f9fa",
                                        }}
                                      >
                                        <i className="fas fa-bed me-2"></i>
                                        Room {roomIdx + 1}
                                      </td>
                                    </tr>
                                    {travellerDetails
                                      .slice(
                                        currentRoomPointer,
                                        currentRoomPointer + totalGuestsInRoom,
                                      )
                                      .map((traveller, idx) => (
                                        <tr key={idx}>
                                          <td className="small py-3 px-4 text-muted">
                                            {traveller.type} {idx + 1}
                                          </td>
                                          <td className="small py-3 px-4 fw-bold text-primary">
                                            {traveller.firstName || "-"}{" "}
                                            {traveller.lastName || "-"}
                                          </td>
                                          <td className="small py-3 px-4 text-dark">
                                            {traveller.age ? `${traveller.age} Years` : "-"}
                                          </td>
                                          {(pricingData?.data?.rooms?.[0]
                                            ?.isPanRequired ||
                                            travellerDetails.some(
                                              (t) => t.pan,
                                            )) && (
                                            <td className="small py-3 px-4 text-dark">
                                              {traveller.pan || "-"}
                                            </td>
                                          )}
                                          {(pricingData?.data?.rooms?.[0]
                                            ?.isPassportRequired ||
                                            travellerDetails.some(
                                              (t) => t.passport,
                                            )) && (
                                            <td className="small py-3 px-4 text-dark">
                                              {traveller.passport || "-"}
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                  </React.Fragment>
                                );
                              },
                            );
                          })()
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              className="text-center py-4 text-muted small"
                            >
                              No traveller details provided.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {gstNumber && (
                    <>
                      <h3 className="sub_headings" style={{ color: "#110942" }}>
                        GST Details
                      </h3>
                      <div className="table-responsive mb-4 shadow-sm border rounded overflow-hidden">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="bg-light">
                            <tr style={{ background: "#f8f9fa" }}>
                              <th className="fw-bold small py-3 px-4">
                                GST Number
                              </th>
                              <th className="fw-bold small py-3 px-4">
                                Company Name
                              </th>
                              <th className="fw-bold small py-3 px-4">Email</th>
                              <th className="fw-bold small py-3 px-4">Phone</th>
                              <th className="fw-bold small py-3 px-4">
                                Address
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="small py-3 px-4 fw-bold text-primary">
                                {gstNumber}
                              </td>
                              <td className="small py-3 px-4 text-dark">
                                {gstName}
                              </td>
                              <td className="small py-3 px-4 text-dark">
                                {gstEmail}
                              </td>
                              <td className="small py-3 px-4 text-dark">
                                {gstPhone}
                              </td>
                              <td className="small py-3 px-4 text-dark">
                                {gstAddress}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
                    <button
                      className="btn btn-link text-decoration-none fw-bold p-0"
                      style={{ color: "#110942", fontSize: "16px" }}
                      onClick={() => {
                        setActiveStep(1);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Back
                    </button>
                    <div className="d-flex align-items-center gap-3">
                      {pricingData?.isHold && isTmc && (
                        <button
                          className="btn btn-warning px-5 rounded-pill"
                          style={{
                            height: "45px",
                            fontWeight: "600",
                            color: "white",
                          }}
                          onClick={handleHoldBooking}
                          disabled={isHoldingBooking || isProcessingPayment}
                        >
                          {isHoldingBooking ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Holding...
                            </>
                          ) : (
                            "Hold Booking"
                          )}
                        </button>
                      )}
                      {ApprovalRequestButton}
                    </div>
                  </div>
                </div>
              )}

              {activeStep === 3 && (
                <div style={{background:"white",borderRadius:8,padding:10}}>
                    <div className="d-flex flex-wrap justify-content-start align-items-center gap-3 mb-4">
                    <button
                      className={`btn ${activeStep === 1 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      onClick={() => activeStep > 1 && setActiveStep(1)}
                      style={{
                        borderRadius: "5px",
                        padding: "10px 25px",
                        fontWeight: "600",
                        background: activeStep === 1 ? "#110942" : "#fff",
                        color: activeStep === 1 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                      }}
                    >
                      TRAVELLERS' DETAILS
                    </button>
                    <button
                      className={`btn ${activeStep === 2 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      onClick={() => activeStep > 1 && setActiveStep(2)}
                      style={{
                        borderRadius: "5px",
                        padding: "10px 25px",
                        fontWeight: "600",
                        background: activeStep === 2 ? "#110942" : "#fff",
                        color: activeStep === 2 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                      }}
                    >
                      CONFIRM BOOKING
                    </button>
                    <button
                      className={`btn ${activeStep === 3 ? "btn-dark shadow" : "btn-outline-secondary bg-white"}`}
                      style={{
                        borderRadius: "5px",
                        padding: "8px 15px",
                        fontWeight: "600",
                        background: activeStep === 3 ? "#110942" : "#fff",
                        color: activeStep === 3 ? "#fff" : "#110942",
                        border: "1px solid #ddd",
                        opacity: activeStep < 3 ? 0.7 : 1,
                      }}
                    >
                      PAYMENT
                    </button>
                  </div>
                  <div className="col-12 col-md-12 col-lg-12">
                    <h4 className="sub_heading">Payment</h4>
                    <div className="row">
                      <div className="col-12 col-lg-12">
                        <div className="paymnt">
                          <div className="paymnt_lft">
                            <ul className="nav nav-tabs paynt" role="tablist">
                              <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="tab" href="#AgencyCredit">Company Billing</a>
                              </li>
                              <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" href="#CreditCard">Credit Card</a>
                              </li>
                              <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" href="#DebitCard">Debit Card</a>
                              </li>
                              <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" href="#NetBanking">Net Banking</a>
                              </li>
                              <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" href="#UPI">UPI</a>
                              </li>
                            </ul>
                          </div>
                          <div className="paymnt_Rgt">
                            <div className="tab-content">
                              <div id="AgencyCredit" className="container tab-pane active">
                                <div className="balnce">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="paymentMethod"
                                    id="agencyCreditRadio"
                                    value="AgencyCreditLimit"
                                    checked={paymentMethod === "AgencyCreditLimit"}
                                    onChange={(e) => {
                                      setPaymentMethod(e.target.value)
                                      setPaymentError("");
                                    }}
                                  />
                                  <i className="fa fa-inr"></i>
                                  {Math.round(
                                    parseFloat(corporateInfo?.creditLimit || 0) +
                                    parseFloat(organizationsDetails?.cashBalance || corporateInfo?.availableBalance || 0)
                                  ).toLocaleString()}
                                </div>
                              </div>
                              <div id="CreditCard" className="container tab-pane fade">
                                <div className="balnce">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="HdfcBank"
                                    id="HdfcBankRadio"
                                    value="HdfcBank"
                                    checked={paymentMethod === "HdfcBank"}
                                    onChange={(e) => {
                                      setPaymentMethod(e.target.value)
                                      setPaymentError("");
                                    }}
                                  />
                                  <img src="/assets/images/hdfc-logo.png" alt="payment" /> 
                                </div>
                              </div>
                              <div id="DebitCard" className="container tab-pane fade">
                                <div className="balnce">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="paymentMethod"
                                    id="debitCardRadio"
                                    value="DebitCard"
                                    checked={paymentMethod === "DebitCard"}
                                    onChange={(e) => {
                                      setPaymentMethod(e.target.value)
                                      setPaymentError("");
                                    }}
                                  />
                                  <i className="fa fa-inr"></i>36565
                                </div>
                              </div>
                              <div id="NetBanking" className="container tab-pane fade"></div>
                              <div id="UPI" className="container tab-pane fade"></div>
                            </div>
                          </div>
                        </div>
                        {paymentError && (
                          <div className="alert alert-danger fw-bold mt-3 py-2">
                            {paymentError}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-md-12 mt-5 mb-5">
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            id="termsCheckbox"
                            name="Status"
                            type="checkbox"
                            checked={isAgreed}
                            onChange={(e) => {
                              setIsAgreed(e.target.checked);
                              if (e.target.checked) setTermsError("");
                            }}
                          />
                          <label className="form-check-label Active" htmlFor="termsCheckbox"> By clicking this button, I understand &amp; accept with the Rules &amp; Restrictions of this fare, the Privacy Policy
                            <span data-bs-toggle="modal" data-bs-target="#term-cond" className="weak ms-1" style={{cursor:'pointer', color:"#295eff"}}>Terms &amp; Conditions</span>
                          </label>
                        </div>
                        {termsError && (
                          <div className="alert alert-danger fw-bold mt-3 py-2">
                            {termsError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {success && (
                    <div className="col-12 col-md-12 mt-3 mb-4">
                      <div className="alert alert-success text-center fw-bold py-2 rounded">
                        {success}
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3">
                    <button
                      className="btn-btn-border"
                      onClick={() => {
                        setActiveStep(2);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Back
                    </button>
                    <div className="d-flex align-items-center gap-3">
                      {paymentButton}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="col-12 col-md-3 po_sticky">
              <div className="box_shadow sticky">
                <h6>Fare Summary</h6>
                <div className="accordion" id="kt_accordion_1">
                  <div className="accordion-item">
                    <h2
                      className="accordion-header"
                      id="kt_accordion_1_header_1"
                    >
                      <button
                        className="accordion-button fw-semibold"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#kt_accordion_1_body_1"
                        aria-expanded="true"
                        aria-controls="kt_accordion_1_body_1"
                      >
                        <div className="flex">
                          <span> Room Price</span>
                          <span>
                            <i className="fa fa-inr"></i>{" "}
                            {displayBasePrice.toLocaleString()}
                          </span>
                        </div>
                      </button>
                    </h2>
                    <div
                      id="kt_accordion_1_body_1"
                      className="accordion-collapse collapse show"
                      aria-labelledby="kt_accordion_1_header_1"
                      data-bs-parent="#kt_accordion_1"
                    >
                      <div className="accordion-body">
                        {(() => {
                          const totalAdults = searchData.roomGuests.reduce(
                            (acc, r) => acc + r.noOfAdults,
                            0,
                          );
                          const totalChildren = searchData.roomGuests.reduce(
                            (acc, r) => acc + (r.noOfChilds || 0),
                            0,
                          );
                          return (
                            <>
                              <div className="flex">
                                <span> Room Price  <p style={{fontSize:"10px"}}>(inclusive of taxes)</p></span>
                                <span>
                                  <i className="fa fa-inr"></i>{" "}
                                  {displayBasePrice.toLocaleString()}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2
                      className="accordion-header"
                      id="kt_accordion_1_header_2"
                    >
                      <button
                        className="accordion-button fw-semibold collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#kt_accordion_1_body_2"
                        aria-expanded="false"
                        aria-controls="kt_accordion_1_body_2"
                      >
                        <div className="flex">
                          <span>Fee & Surcharges</span>
                          <span>
                            {" "}
                            <i className="fa fa-inr"></i>{" "}
                            {displayTaxesAndFees.toLocaleString()}
                          </span>
                        </div>
                      </button>
                    </h2>
                    <div
                      id="kt_accordion_1_body_2"
                      className="accordion-collapse collapse"
                      aria-labelledby="kt_accordion_1_header_2"
                      data-bs-parent="#kt_accordion_1"
                    >
                      <div className="accordion-body">
                        <div className="flex">
                          <span> Tax</span>
                          <span>
                            <i className="fa fa-inr"></i>{" "}
                            {Math.round(apiTaxAmount).toLocaleString()}
                          </span>
                        </div>
                        {apiGstAmount > 0 && (
                          <div className="flex">
                            <span> GST (Inclusive)</span>
                            <span>
                              <i className="fa fa-inr"></i>{" "}
                              {Math.round(apiGstAmount).toLocaleString()}
                            </span>
                          </div>
                        )}

                        

                        <div className="flex">
                          <span> Management Fees</span>
                          <span>
                            <i className="fa fa-inr"></i>
                            {Math.round(managementFee).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex">
                          <span> Management Fees GST</span>
                          <span>
                            <i className="fa fa-inr"></i>
                            {Math.round(managementFeeTax).toLocaleString()}
                          </span>
                        </div>
                     
                      </div>
                    </div>
                  </div>

                  {isTmc && (
                    <div className="accordion-item">
                      <h2
                        className="accordion-header"
                        id="kt_accordion_1_header_5"
                      >
                        <button
                          className="accordion-button fw-semibold collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#kt_accordion_1_body_5"
                        >
                          <div className="flex">
                            <span>Modify Mark-Up</span>
                          </div>
                        </button>
                      </h2>

                      <div
                        id="kt_accordion_1_body_5"
                        className="accordion-collapse collapse"
                      >
                        <div className="accordion-body">
                          <div className="flex">
                            <span className="markup">Mark Up</span>
                            <span className="inpt">
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Enter markup"
                                value={manualMarkup}
                                onChange={(e) =>
                                  setManualMarkup(Number(e.target.value) || 0)
                                }
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="d-flexes">
                  <div className="totals">GRAND TOTAL</div>
                  <div className="tolt_price">
                    <i className="fa fa-inr"></i>
                    <span>{grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancellation Policy Modal */}
      {showCancellationModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
              <div
                className="modal-header border-0 p-4 d-flex align-items-center justify-content-between"
                style={{ background: "#110942" }}
              >
                <h5 className="modal-title fw-bold text-white mb-0">
                  <i className="fas fa-info-circle me-2 text-warning"></i>
                  Cancellation Policy
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white shadow-none"
                  onClick={() => setShowCancellationModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                {(() => {
                  const policies = pricingRoom?.cancellationPolicy || [];
                  const isRoomRefundable = policies.some(
                    (p) => p.penaltyAmount === 0,
                  );
                  const formatDateLocal = (dateStr) => {
                    if (!dateStr) return "-";
                    const d = new Date(dateStr);
                    return d.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
                  };

                  return (
                    <>
                      <div className="policy-info mb-4">
                        <p className="text-muted small lh-base mb-3">
                          {isRoomRefundable
                            ? "Cancellation policies vary by room type and provider. Please review the penalty structure below carefully."
                            : "This room is Non-Refundable. No refund will be provided for cancellations."}
                        </p>
                        <div className="table-responsive rounded border mb-4">
                          <div
                            className="d-flex w-100 shadow-sm"
                            style={{
                              height: "30px",
                              borderRadius: "15px",
                              overflow: "hidden",
                            }}
                          >
                            {isRoomRefundable ? (
                              <>
                                <div
                                  className="timeline-segment-success"
                                  style={{
                                    flex: "0 0 50%",
                                    background: "#28a745",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  100% Refund
                                </div>
                                <div
                                  className="timeline-segment-danger"
                                  style={{
                                    flex: "0 0 50%",
                                    background: "#dc3545",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Non-Refundable
                                </div>
                              </>
                            ) : (
                              <div
                                className="timeline-segment-danger"
                                style={{
                                  flex: "0 0 100%",
                                  background: "#dc3545",
                                  color: "white",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                }}
                              >
                                Non-Refundable
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="table-responsive rounded border">
                          <table className="table table-sm align-middle mb-0 text-center">
                            <thead className="bg-light text-uppercase x-small">
                              <tr
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                <th className="py-2 px-3 border-0">
                                  From Date
                                </th>
                                <th className="py-2 px-3 border-0">To Date</th>
                                <th className="py-2 px-3 border-0">
                                  Penalty Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody className="small">
                              {policies.length > 0 ? (
                                policies.map((p, i) => (
                                  <tr key={i}>
                                    <td className="py-2 px-3">
                                      {formatDateLocal(p.fromDate)}
                                    </td>
                                    <td className="py-2 px-3">
                                      {formatDateLocal(p.toDate)}
                                    </td>
                                    <td className="py-2 px-3 fw-bold text-danger">
                                      {p.penaltyAmount === 0
                                        ? "₹ 0 (Free)"
                                        : `${p.currency || "₹"} ${Math.round(p.penaltyAmount).toLocaleString()}`}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan="3"
                                    className="text-center py-3 text-muted"
                                  >
                                    No policy data available
                                  </td>
                                </tr>
                              )}
                              <tr className="bg-light">
                                <td
                                  colSpan="3"
                                  className="text-center py-2 text-muted x-small"
                                  style={{ fontSize: "11px" }}
                                >
                                  No Show will attract full cancellation charge
                                  unless otherwise specified.
                                </td>
                              </tr>
                              <tr className="bg-light">
                                <td
                                  colSpan="3"
                                  className="text-center py-2 text-muted x-small"
                                  style={{ fontSize: "11px" }}
                                >
                                  Early check out will attract full cancellation
                                  charge unless otherwise specified.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer border-0 p-3">
                <button
                  type="button"
                  className="btn-choose-room w-100"
                  onClick={() => setShowCancellationModal(false)}
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="modal fade" id="term-cond" tabIndex="-1" aria-labelledby="termCondLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content" style={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>
            <div className="modal-header" style={{ borderBottom: "1px solid #eee", padding: "20px" }}>
              <h5 className="modal-title fw-bold" id="termCondLabel" style={{ color: "#110942" }}>Terms &amp; Conditions</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body" style={{ padding: "25px", maxHeight: "400px", overflowY: "auto", fontSize: "14px", color: "#555", lineHeight: "1.6" }}>
              <h6 className="fw-bold mb-3" style={{ color: "#110942" }}>1. Booking and Reservation Policy</h6>
              <p>All bookings made through this platform are subject to availability and confirmation by the hotel. Once confirmed, booking details, including guest names and room categories, cannot be modified without contacting customer support, and may be subject to additional charges.</p>
              
              <h6 className="fw-bold mb-3 mt-4" style={{ color: "#110942" }}>2. Cancellation and Refund Policy</h6>
              <p>Cancellation policies vary by hotel and room category. Please refer to the specific Cancellation Policy shown during the booking process. Cancellations made after the free cancellation deadline will attract charges as specified. No-shows (failure to check in) will be charged the full booking amount.</p>
              
              <h6 className="fw-bold mb-3 mt-4" style={{ color: "#110942" }}>3. Hotel Check-In and Check-Out Rules</h6>
              <p>Standard check-in and check-out times must be followed. Early check-in or late check-out requests are subject to hotel availability and may incur extra fees. Guests must present a valid government-issued photo ID and credit/debit card or deposit at the time of check-in.</p>
              
              <h6 className="fw-bold mb-3 mt-4" style={{ color: "#110942" }}>4. Taxes and Additional Charges</h6>
              <p>Room rates include room charges and specified taxes. Any additional expenses incurred during the stay (such as room service, minibar, telephone calls, laundry, parking, spa, etc.) must be settled directly with the hotel prior to check-out.</p>
              
              <h6 className="fw-bold mb-3 mt-4" style={{ color: "#110942" }}>5. Liability Limitation</h6>
              <p>We act as an agent connecting guests with hotels and are not responsible for any disputes, services, amenities, or conditions of the property. Guests agree to abide by the rules and regulations of the respective hotels.</p>
            </div>
            <div className="modal-footer" style={{ borderTop: "1px solid #eee", padding: "15px 20px" }}>
              <button type="button" className="btn btn-secondary px-4 rounded-pill" data-bs-dismiss="modal" style={{ fontWeight: "600" }}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
